import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";
import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CommunityApi from "../../shared/api/CommunityApi";
import { sanitizeSvg } from "../../components/SvgDrawing/SvgDrawing";
import { Announcement, CtaCard, Intermission, IntermissionInput, SocialLink } from "../../shared/interfaces/GameLogicTypes";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import StyleIcon from "@mui/icons-material/Style";
import PeopleIcon from "@mui/icons-material/People";
import StarIcon from "@mui/icons-material/Star";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import FavoriteIcon from "@mui/icons-material/Favorite";
import CampaignIcon from "@mui/icons-material/Campaign";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import ShareIcon from "@mui/icons-material/Share";
import CardDeckViewer from "../../components/CardDeckViewer/CardDeckViewer";
import SvgDrawing from "../../components/SvgDrawing/SvgDrawing";
import { LineChart as _LineChart, Line as _Line, XAxis as _XAxis, YAxis as _YAxis, CartesianGrid as _CartesianGrid, Tooltip as _Tooltip, ResponsiveContainer as _ResponsiveContainer } from "recharts";
import { serverUrl, socketNamespace } from "../../shared/runtime";

// Cast recharts components to work around React 17 type incompatibility
const RLineChart = _LineChart as any;
const RLine = _Line as any;
const RXAxis = _XAxis as any;
const RYAxis = _YAxis as any;
const RCartesianGrid = _CartesianGrid as any;
const RTooltip = _Tooltip as any;
const RResponsiveContainer = _ResponsiveContainer as any;

const apiFetch = (path: string, init?: RequestInit) =>
  fetch(serverUrl(path), init);

// ────────────────────────── Types ──────────────────────────

interface Player {
  name: string;
  score: number;
  remainingSkips: number;
  joinTime: number;
  categoryWeights: Record<string, number>;
  config: Record<string, number>;
}

interface Cue {
  type: number;
  content: string | number;
}

interface TurnInfo {
  tsarId: string | null;
  question: { id: number; forTsar: string; aboutTsar?: string };
  cues: Cue[];
  startTime: number;
  timer: number;
  submissions: Record<string, number[]>;
  submissionTimes: Record<string, number>;
  versusGroupRanking?: number[];
}

interface FtlHistorySubmission {
  choice: number;
  wasAlive: boolean;
  survived: boolean;
  points: number;
}

interface FtlHistoryData {
  streakId: string;
  streakIndex: number;
  optionCount: number;
  czarChoice: number;
  multiplier: number;
  submissions: Record<string, FtlHistorySubmission>;
}

interface HerdHistorySubmission {
  choice: number | null;
  wasAlive: boolean;
  survived: boolean;
  points: number;
}

interface HerdHistoryData {
  streakId: string;
  streakIndex: number;
  optionCounts: number[];
  wasSplit: boolean;
  submissions: Record<string, HerdHistorySubmission>;
}

interface RrGuess {
  playerId: string;
  playerName: string;
  guessedId: string | null;
  guessedName: string | null;
  correct: boolean;
  points: number;
}

interface RrSequenceCard {
  sequence: number[];
  submitterId: string | null;
  submitterName: string;
  isRandom: boolean;
  shownTo: string[];
  guesses: RrGuess[];
}

interface RrHistoryData {
  sequences: RrSequenceCard[];
  playerPoints: Record<string, number>;
  ownerBonuses: Record<string, number>;
}

interface PicassoHistoryData {
  drawings: { playerId: string; playerName: string; drawingSvg?: string; correctGuessCount: number; pointsEarned: number; isImposter?: boolean; imposterTarget?: string; pointBreakdown: string }[];
  challengeResults: { type: string; targetPlayerName: string; drawerAName: string; drawerBName: string; drawingA?: string; drawingB?: string; realLabel: string; imposterName?: string; voters: { playerName: string; vote: string; correct: boolean; points: number }[] }[];
  imposterResults: { imposterName: string; targetName: string; accepted: boolean; fooledCount: number; imposterPoints: number }[];
  playerPoints: Record<string, number>;
}

interface TurnHistoryEntry {
  question: { id: number; forTsar: string };
  tsarId: string | null;
  cues: Cue[];
  startTime: number;
  endTime?: number;
  submissions: Record<string, number[]>;
  submissionTimes: Record<string, number>;
  versusGroupRanking?: number[];
  pointsPerPlayer: Record<string, number>;
  turnMode?: string;
  skipped?: boolean;
  ftlData?: FtlHistoryData;
  herdData?: HerdHistoryData;
  rrData?: RrHistoryData;
  picassoData?: PicassoHistoryData;
}

interface FtlState {
  streakIndex: number;
  czarId: string;
  aliveCount: number;
  deadCount: number;
  multiplier: number;
}

interface HerdState {
  streakIndex: number;
  aliveCount: number;
  deadCount: number;
}

interface AdminLobbySnapshot {
  lobbyKey: string;
  gamePhase: string;
  turnPhase: string;
  hostId: string;
  round: number;
  config: {
    roundTimer: number;
    roundLimit: number;
    maxSkips: number;
    allAboutMe: boolean;
    waitForHost: boolean;
    enableJoinDuringRound: boolean;
    enabledModes?: string[];
    preset?: string;
    entryMode?: "VERSUS" | "COOP";
    deckMode?: string;
    selectedTopics?: string[];
  };
  players: Record<string, Player>;
  leftPlayers?: Record<string, string>;
  connectedPlayerIds: string[];
  selectedDeckIds: string[];
  deckNames: Record<string, string>;
  currentTurnMode?: string;
  resultCards?: Array<{ type: number; title: string; subtitle?: string; body: string; data: Record<string, unknown>; priority: number }>;
  ftlState?: FtlState | null;
  herdState?: HerdState | null;
  reverseRoundState?: {
    stage: 1 | 2;
    submissionCount: number;
    guessCount: number;
    highRollEnabled: boolean;
  } | null;
  picassoState?: {
    stage: 1 | 2;
    drawingCount: number;
    challengeCount: number;
    assignmentCount: number;
    voteCount: number;
    imposterCount: number;
    totalPlayers: number;
    question: string;
    timer: number;
    startTime: number;
    imposterDetails: { imposterId: string; imposterName: string; targetName: string; accepted: boolean; decided: boolean }[];
    drawingSubmitters: string[];
    voterDetails: { playerName: string; voted: boolean }[];
  } | null;
  turnInfo: TurnInfo | null;
  turnHistory: TurnHistoryEntry[];
}

// ────────────────────────── Helpers ──────────────────────────

function phaseLabel(gamePhase: string) {
  if (gamePhase === "L") return "LOBBY";
  if (gamePhase === "R") return "RUNNING";
  if (gamePhase === "F") return "FINISHED";
  return gamePhase;
}

function phaseColor(
  gamePhase: string
): "default" | "success" | "primary" | "error" {
  if (gamePhase === "L") return "default";
  if (gamePhase === "R") return "success";
  if (gamePhase === "F") return "error";
  return "primary";
}

function elapsedSeconds(startTime: number) {
  return Math.floor((Date.now() - startTime) / 1000);
}

function fmtDate(d: string | Date) {
  return new Date(d).toLocaleString();
}

// ────────────────────────── Sub-components ──────────────────────────

// Fixed colours matching the game's 5 card slots
const CUE_BG = ["#fff355", "#608ad6", "#e75151", "#92dc67", "#e17d47"];
const CUE_FG = ["#333333", "#ffffff", "#ffffff", "#ffffff", "#ffffff"];

function cueFontSize(text: string) {
  const len = text.length;
  if (len <= 18) return 12;
  if (len <= 35) return 10;
  if (len <= 60) return 8.5;
  return 7;
}

// Every turn mode the game can produce, with the label + colour used wherever a turn is shown.
const TURN_MODE_META: Record<string, { label: string; color: string }> = {
  STANDARD:          { label: "STANDARD", color: "#1976d2" },
  STANDARD_VERSUS:   { label: "VERSUS",   color: "#0097a7" },
  FOLLOW_THE_LEADER: { label: "FTL",      color: "#d32f2f" },
  REVERSE_ROUND:     { label: "RR",       color: "#9c27b0" },
  RUN_WITH_THE_HERD: { label: "HERD",     color: "#2e7d32" },
  PICASSO:           { label: "PICASSO",  color: "#ff9800" },
};

/** A turn with no recorded mode predates multi-mode logging and was always a co-op STANDARD turn. */
function turnModeMeta(mode: string | undefined) {
  return TURN_MODE_META[mode ?? "STANDARD"] ?? { label: mode ?? "UNKNOWN", color: "#616161" };
}

function isVersusMode(mode: string | undefined) {
  return mode === "STANDARD_VERSUS";
}

function formatResponseTime(ms: number | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return "—";
  return `${(Math.max(0, ms) / 1000).toFixed(1)}s`;
}

function TurnModeChip({ mode, sx }: { mode: string | undefined; sx?: object }) {
  const { label, color } = turnModeMeta(mode);
  return (
    <Chip
      label={label}
      size="small"
      sx={{ height: 16, fontSize: 9, bgcolor: color, color: "#fff", ...sx }}
    />
  );
}

const CueChip = React.memo(function CueChip({ cueIndex, label }: { cueIndex: number; label: string }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: CUE_BG[cueIndex] ?? "#ccc",
        color: CUE_FG[cueIndex] ?? "#000",
        borderRadius: 1,
        p: 0.5,
        fontSize: cueFontSize(label),
        fontWeight: 600,
        lineHeight: 1.3,
        whiteSpace: "normal",
        wordBreak: "break-word",
        textAlign: "center",
        width: 120,
        height: 56,
        flexShrink: 0,
        overflow: "hidden",
      }}
    >
      {label}
    </Box>
  );
});

// Sorted standings table — pass cumulativeScores for history rows, or players for current
const Scoreboard = React.memo(function Scoreboard({
  players,
  cumulativeScores,
  compact = false,
}: {
  players: Record<string, Player>;
  cumulativeScores?: Record<string, number>;
  compact?: boolean;
}) {
  const entries = Object.entries(players)
    .map(([pid, p]) => ({ pid, name: p.name, score: cumulativeScores ? (cumulativeScores[pid] ?? 0) : p.score }))
    .sort((a, b) => b.score - a.score);

  return (
    <Table size="small" sx={{ mb: compact ? 0 : 1 }}>
      {!compact && (
        <TableHead>
          <TableRow>
            <TableCell sx={{ py: 0.3, fontWeight: "bold" }}>#</TableCell>
            <TableCell sx={{ py: 0.3, fontWeight: "bold" }}>Player</TableCell>
            <TableCell sx={{ py: 0.3, fontWeight: "bold" }} align="right">Score</TableCell>
          </TableRow>
        </TableHead>
      )}
      <TableBody>
        {entries.map((e, rank) => (
          <TableRow key={e.pid}>
            <TableCell sx={{ py: 0.2, width: 24, color: "text.secondary" }}>{rank + 1}</TableCell>
            <TableCell sx={{ py: 0.2 }}>{e.name}</TableCell>
            <TableCell sx={{ py: 0.2 }} align="right">{e.score}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
});

const TurnHistorySection = React.memo(function TurnHistorySection({
  turnHistory,
  players,
  leftPlayers,
}: {
  turnHistory: TurnHistoryEntry[];
  players: Record<string, Player>;
  leftPlayers?: Record<string, string>;
}) {
  const pn = (pid: string | null) => pid == null ? "—" : (players[pid]?.name ?? leftPlayers?.[pid] ?? pid);
  const [open, setOpen] = useState(false);

  // Build cumulative scores after each turn
  const cumulativeByTurn = useMemo(() => {
    const result: Record<string, number>[] = [];
    for (let i = 0; i < turnHistory.length; i++) {
      const prev = i === 0 ? {} : result[i - 1];
      const next: Record<string, number> = { ...prev };
      for (const [pid, pts] of Object.entries(turnHistory[i].pointsPerPlayer ?? {})) {
        next[pid] = (next[pid] ?? 0) + pts;
      }
      result.push(next);
    }
    return result;
  }, [turnHistory]);

  // Group consecutive FTL and Herd turns by streakId; group same-tsar STANDARD/skipped turns
  type RenderedItem =
    | { type: "tsarGroup"; tsarId: string | null; mode: string | undefined; turns: { turnIndex: number; turn: TurnHistoryEntry }[] }
    | { type: "ftlStreak"; streakId: string; turns: { turnIndex: number; turn: TurnHistoryEntry }[] }
    | { type: "herdStreak"; streakId: string; turns: { turnIndex: number; turn: TurnHistoryEntry }[] };

  const rendered = useMemo(() => {
    const result: RenderedItem[] = [];
    for (let i = 0; i < turnHistory.length; i++) {
      const turn = turnHistory[i];
      if (turn.turnMode === "FOLLOW_THE_LEADER" && turn.ftlData) {
        const last = result[result.length - 1];
        if (last && last.type === "ftlStreak" && last.streakId === turn.ftlData.streakId) {
          last.turns.push({ turnIndex: i, turn });
        } else {
          result.push({ type: "ftlStreak", streakId: turn.ftlData.streakId, turns: [{ turnIndex: i, turn }] });
        }
      } else if (turn.turnMode === "RUN_WITH_THE_HERD" && turn.herdData) {
        const last = result[result.length - 1];
        if (last && last.type === "herdStreak" && last.streakId === turn.herdData.streakId) {
          last.turns.push({ turnIndex: i, turn });
        } else {
          result.push({ type: "herdStreak", streakId: turn.herdData.streakId, turns: [{ turnIndex: i, turn }] });
        }
      } else {
        // Only regular co-op turns form a multi-turn tsar group. No-tsar modes such as Versus
        // and Picasso must remain separate; otherwise reversing the groups for this live view
        // leaves every turn inside their one giant group in oldest-first order.
        const last = result[result.length - 1];
        const groupTsarId = isVersusMode(turn.turnMode) ? null : turn.tsarId;
        const canJoinTsarGroup = turn.turnMode == null || turn.turnMode === "STANDARD";
        if (
          canJoinTsarGroup &&
          last &&
          last.type === "tsarGroup" &&
          last.tsarId === groupTsarId &&
          last.mode === turn.turnMode
        ) {
          last.turns.push({ turnIndex: i, turn });
        } else {
          result.push({ type: "tsarGroup", tsarId: groupTsarId, mode: turn.turnMode, turns: [{ turnIndex: i, turn }] });
        }
      }
    }
    return result;
  }, [turnHistory]);

  const [visibleCount, setVisibleCount] = useState(5);
  const reversedRendered = useMemo(() => [...rendered].reverse(), [rendered]);
  const visibleItems = reversedRendered.slice(0, visibleCount);
  const hasMore = visibleCount < reversedRendered.length;

  if (turnHistory.length === 0) return null;

  return (
    <Box mt={2}>
      <Button size="small" onClick={() => setOpen((o) => !o)} variant="outlined">
        {open ? "Hide" : "Show"} Turn History ({turnHistory.length})
      </Button>
      <Collapse in={open}>
        <Box mt={1}>
          {visibleItems.map((item, ri) => {
            // ── Same-tsar group: STANDARD + skipped + RR ──
            if (item.type === "tsarGroup") {
              const { tsarId, mode: groupMode, turns: groupTurns } = item;
              const tsarName = pn(tsarId);
              const versus = isVersusMode(groupMode);
              const allPlayerIds = Object.keys(players);
              // Find last non-skipped turn index for standings
              const lastPlayedEntry = [...groupTurns].reverse().find(e => !e.turn.skipped);
              const lastIndex = lastPlayedEntry?.turnIndex ?? groupTurns[groupTurns.length - 1].turnIndex;
              // Is this group exclusively RR or Picasso (no standard turns)?
              const isRRGroup = groupTurns.every(e => e.turn.turnMode === "REVERSE_ROUND" && !e.turn.skipped);
              const isPicassoGroup = groupTurns.every(e => e.turn.turnMode === "PICASSO" && !e.turn.skipped);
              const specialBorder = isRRGroup ? "#9c27b0" : isPicassoGroup ? "#ff9800" : null;

              return (
                <Paper key={`tg-${tsarId}-${groupTurns[0].turnIndex}`} variant="outlined" sx={{ p: 1.5, mb: 1.5, ...(specialBorder ? { borderLeft: `3px solid ${specialBorder}` } : {}) }}>
                  {/* Header */}
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <TurnModeChip mode={isRRGroup ? "REVERSE_ROUND" : isPicassoGroup ? "PICASSO" : groupMode} />
                    {!isRRGroup && !isPicassoGroup && !versus && (
                      <Typography variant="caption" color="text.secondary">
                        Tsar: <strong>{tsarName}</strong>
                      </Typography>
                    )}
                  </Box>

                  {groupTurns.map(({ turnIndex: i, turn }) => {
                    // ── Skipped turn: compact inline row ──
                    if (turn.skipped) {
                      return (
                        <Box key={`skip-${i}`} display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.5, opacity: 0.6 }}>
                          <Typography variant="caption" color="text.secondary" fontWeight="bold">Turn {i + 1}</Typography>
                          <TurnModeChip mode={turn.turnMode} />
                          <Chip label="SKIPPED" size="small" color="warning" sx={{ height: 14, fontSize: 9 }} />
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {turn.question.forTsar}
                          </Typography>
                        </Box>
                      );
                    }

                    // ── Reverse Round turn ──
                    if (turn.turnMode === "REVERSE_ROUND") {
                      return (
                        <Box key={`rr-${i}`} sx={{ mt: groupTurns.length > 1 ? 1 : 0 }}>
                          <Box display="flex" alignItems="baseline" gap={1} mb={0.5}>
                            <Typography variant="caption" fontWeight="bold">Turn {i + 1}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>{turn.question.forTsar}</Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary" gutterBottom>All players' truthful rankings:</Typography>
                          <Box display="flex" flexDirection="column" gap={0.5} mb={1}>
                            {allPlayerIds.map((pid) => {
                              const name = pn(pid);
                              const seq = turn.submissions[pid];
                              return (
                                <Box key={pid} sx={{ mb: 0.5 }}>
                                  <Typography variant="caption" fontWeight="bold">{name}</Typography>
                                  <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.3}>
                                    {seq && seq.length > 0 ? (
                                      seq.map((cueNum, pos) => (
                                        <CueChip key={pos} cueIndex={cueNum - 1} label={String(turn.cues[cueNum - 1]?.content ?? cueNum)} />
                                      ))
                                    ) : (
                                      <Typography variant="caption" color="text.disabled" sx={{ fontStyle: "italic" }}>- no answer</Typography>
                                    )}
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                          {turn.rrData && turn.rrData.sequences.length > 0 && (
                            <>
                              <Divider sx={{ my: 1 }} />
                              <Typography variant="caption" color="text.secondary" gutterBottom>Guessing phase:</Typography>
                              <Box display="flex" flexDirection="column" gap={1} mt={0.5}>
                                {turn.rrData.sequences.map((card, si) => (
                                  <Box key={si} sx={{ pl: 1, borderLeft: "2px solid #ce93d8" }}>
                                    <Box display="flex" alignItems="center" gap={0.5} mb={0.3}>
                                      <Typography variant="caption" fontWeight="bold" color="#9c27b0">
                                        {card.isRandom ? "Random sequence" : card.submitterName}
                                      </Typography>
                                      {card.isRandom && <Chip label="random" size="small" sx={{ height: 14, fontSize: 9 }} />}
                                    </Box>
                                    {card.guesses.length === 0 ? (
                                      <Typography variant="caption" color="text.disabled" sx={{ fontStyle: "italic" }}>— nobody guessed this</Typography>
                                    ) : (
                                      <Box display="flex" flexDirection="column" gap={0.2}>
                                        {card.guesses.map((g, gi) => (
                                          <Box key={gi} display="flex" alignItems="center" gap={0.5}>
                                            <Typography variant="caption" sx={{ minWidth: 72 }}>{g.playerName}</Typography>
                                            <Typography variant="caption" color={g.correct ? "success.main" : "error.main"}>
                                              → {g.guessedName ?? "—"}
                                            </Typography>
                                            {g.correct && <Chip label="✓" size="small" color="success" sx={{ height: 14, fontSize: 9 }} />}
                                            <Typography variant="caption" color="text.secondary">+{g.points}</Typography>
                                          </Box>
                                        ))}
                                      </Box>
                                    )}
                                  </Box>
                                ))}
                              </Box>
                            </>
                          )}
                        </Box>
                      );
                    }

                    // ── Picasso turn ──
                    if (turn.turnMode === "PICASSO" && turn.picassoData) {
                      const pic = turn.picassoData;
                      return (
                        <Box key={`pic-${i}`} sx={{ mt: groupTurns.length > 1 ? 1 : 0 }}>
                          <Box display="flex" alignItems="baseline" gap={1} mb={0.5}>
                            <Typography variant="caption" fontWeight="bold">Turn {i + 1}</Typography>
                            <Chip label="PICASSO" size="small" sx={{ height: 16, fontSize: 9, bgcolor: "#ff9800", color: "#fff" }} />
                            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>{turn.question.forTsar}</Typography>
                          </Box>
                          {/* Player drawings */}
                          <Box display="flex" flexWrap="wrap" gap={1.5}>
                            {pic.drawings.map((d) => (
                              <Box key={d.playerId} sx={{ width: 150 }}>
                                {d.drawingSvg && (
                                  <Box sx={{ borderRadius: 1, border: "1px solid rgba(0,0,0,0.12)", overflow: "hidden", bgcolor: "#fff" }}>
                                    <SvgDrawing svg={d.drawingSvg} width={150} height={150} />
                                  </Box>
                                )}
                                <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap" mt={0.5}>
                                  <Typography variant="caption" fontWeight="bold">{d.playerName}</Typography>
                                  {d.correctGuessCount > 0 && (
                                    <Typography variant="caption" color="text.secondary">
                                      {d.correctGuessCount} recognized
                                    </Typography>
                                  )}
                                  <Typography variant="caption" color="#90caf9">+{d.pointsEarned}</Typography>
                                  {d.pointBreakdown && (
                                    <Typography variant="caption" color="#b0bec5" sx={{ fontSize: 9 }}>({d.pointBreakdown})</Typography>
                                  )}
                                  {d.isImposter && (
                                    <Chip label={`imposter → ${d.imposterTarget}`} size="small" sx={{ height: 16, fontSize: 9, bgcolor: "#7b1fa2", color: "#fff" }} />
                                  )}
                                </Box>
                              </Box>
                            ))}
                          </Box>
                          {/* Challenge results */}
                          {pic.challengeResults && pic.challengeResults.length > 0 && (
                            <Box mt={0.5}>
                              <Typography variant="caption" fontWeight="bold" color="text.secondary">Challenges:</Typography>
                              {pic.challengeResults.map((c, ci) => (
                                <Box key={ci} sx={{ pl: 1, mt: 0.3 }}>
                                  <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                                    <Chip
                                      label={c.type}
                                      size="small"
                                      sx={{
                                        height: 16, fontSize: 9,
                                        bgcolor: c.type === "imposter" ? "#7b1fa2" : c.type === "archive" ? "#0288d1" : "#616161",
                                        color: "#fff",
                                      }}
                                    />
                                    <Typography variant="caption">
                                      {c.targetPlayerName}: {c.drawerAName} vs {c.drawerBName} (real={c.realLabel})
                                    </Typography>
                                  </Box>
                                  {(c.drawingA || c.drawingB) && (
                                    <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
                                      {c.drawingA && (
                                        <Box>
                                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9 }}>{c.drawerAName}</Typography>
                                          <Box sx={{ width: 300, height: 300, borderRadius: 1, border: "1px solid rgba(0,0,0,0.12)", overflow: "hidden", bgcolor: "#fff", "& svg": { width: "100%", height: "100%", display: "block" } }}>
                                            <Box component="span" dangerouslySetInnerHTML={{ __html: sanitizeSvg(c.drawingA, `admin-a-${ci}`) }} />
                                          </Box>
                                        </Box>
                                      )}
                                      {c.drawingB && (
                                        <Box>
                                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9 }}>{c.drawerBName}</Typography>
                                          <Box sx={{ width: 300, height: 300, borderRadius: 1, border: "1px solid rgba(0,0,0,0.12)", overflow: "hidden", bgcolor: "#fff", "& svg": { width: "100%", height: "100%", display: "block" } }}>
                                            <Box component="span" dangerouslySetInnerHTML={{ __html: sanitizeSvg(c.drawingB, `admin-b-${ci}`) }} />
                                          </Box>
                                        </Box>
                                      )}
                                    </Box>
                                  )}
                                  {c.voters.map((v, vi) => (
                                    <Typography key={vi} variant="caption" sx={{ pl: 1, display: "block", color: v.correct ? "#66bb6a" : "#ef5350", fontSize: 10 }}>
                                      {v.playerName}: {v.vote} {v.correct ? "\u2713" : "\u2717"} {v.points > 0 ? `+${v.points}` : ""}
                                    </Typography>
                                  ))}
                                </Box>
                              ))}
                            </Box>
                          )}
                          {/* Imposter results */}
                          {pic.imposterResults.length > 0 && (
                            <Box mt={0.5}>
                              {pic.imposterResults.map((imp, ii) => (
                                <Typography key={ii} variant="caption" color="text.secondary" display="block">
                                  {imp.imposterName} → {imp.targetName}: {imp.accepted ? (imp.fooledCount > 0 ? `fooled ${imp.fooledCount}` : "fooled nobody") : "declined"} (+{imp.imposterPoints})
                                </Typography>
                              ))}
                            </Box>
                          )}
                        </Box>
                      );
                    }

                    // ── Standard / versus turn ──
                    const orderedPlayerIds = versus
                      ? allPlayerIds
                      : [tsarId, ...allPlayerIds.filter((pid) => pid !== tsarId)]
                          .filter((pid): pid is string => pid != null);
                    const versusTimingRows = versus
                      ? Object.keys(turn.submissions)
                          .map((pid) => ({
                            pid,
                            ms: turn.submissionTimes?.[pid] == null
                              ? null
                              : Math.max(0, turn.submissionTimes[pid] - turn.startTime),
                          }))
                          .filter((entry): entry is { pid: string; ms: number } => entry.ms != null)
                          .sort((a, b) => a.ms - b.ms)
                      : [];
                    const versusAverageMs = versusTimingRows.length > 0
                      ? versusTimingRows.reduce((sum, entry) => sum + entry.ms, 0) / versusTimingRows.length
                      : null;
                    return (
                      <Box key={`std-${i}`} sx={{ mt: groupTurns.length > 1 ? 1 : 0 }}>
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Typography variant="caption" fontWeight="bold">Turn {i + 1}</Typography>
                          <TurnModeChip mode={turn.turnMode} />
                          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>{turn.question.forTsar}</Typography>
                        </Box>
                        {versus && turn.versusGroupRanking?.length === 5 && (
                          <Box sx={{ mb: 1, pl: 1, borderLeft: "3px solid #0097a7" }}>
                            <Typography variant="caption" fontWeight="bold" color="#0097a7">
                              Final group ranking
                            </Typography>
                            <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.3}>
                              {turn.versusGroupRanking.map((cueNumber, position) => (
                                <CueChip
                                  key={position}
                                  cueIndex={cueNumber - 1}
                                  label={String(turn.cues[cueNumber - 1]?.content ?? cueNumber)}
                                />
                              ))}
                            </Box>
                            {versusTimingRows.length > 0 && (
                              <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                                Average {formatResponseTime(versusAverageMs ?? undefined)}
                                {" · "}Fastest {pn(versusTimingRows[0].pid)} {formatResponseTime(versusTimingRows[0].ms)}
                                {" · "}Slowest {pn(versusTimingRows[versusTimingRows.length - 1].pid)} {formatResponseTime(versusTimingRows[versusTimingRows.length - 1].ms)}
                              </Typography>
                            )}
                          </Box>
                        )}
                        <Box display="flex" flexDirection="column" gap={0.5}>
                          {orderedPlayerIds.map((pid) => {
                            const name = pn(pid);
                            const isTsar = pid === tsarId;
                            const seq = turn.submissions[pid];
                            const responseMs = turn.submissionTimes?.[pid] == null
                              ? undefined
                              : turn.submissionTimes[pid] - turn.startTime;
                            return (
                              <Box key={pid} sx={{ mb: 0.5 }}>
                                <Typography variant="caption" fontWeight="bold">
                                  {name}
                                  {!versus && isTsar && (
                                    <Chip
                                      label="tsar"
                                      size="small"
                                      color="secondary"
                                      sx={{ ml: 0.5, height: 14, fontSize: 9 }}
                                    />
                                  )}
                                  {seq && (
                                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                      {turn.pointsPerPlayer?.[pid] ?? 0} pts
                                      {" · "}{formatResponseTime(responseMs)}
                                    </Typography>
                                  )}
                                </Typography>
                                <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.3}>
                                  {seq && seq.length > 0 ? (
                                    seq.map((cueNum, pos) => (
                                      <CueChip key={pos} cueIndex={cueNum - 1} label={String(turn.cues[cueNum - 1]?.content ?? cueNum)} />
                                    ))
                                  ) : (
                                    <Typography variant="caption" color="text.disabled" sx={{ fontStyle: "italic" }}>- no answer</Typography>
                                  )}
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  })}

                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">Standings after turn {lastIndex + 1}</Typography>
                  <Scoreboard players={players} cumulativeScores={cumulativeByTurn[lastIndex]} compact />
                </Paper>
              );
            }

            // ── Herd streak ──
            if (item.type === "herdStreak") {
              const { streakId: hStreakId, turns: hStreakTurns } = item;
              const hLastTurnIndex = hStreakTurns[hStreakTurns.length - 1].turnIndex;

              return (
                <Paper key={`herd-${hStreakId}`} variant="outlined" sx={{ p: 1.5, mb: 1.5, borderLeft: "3px solid #4caf50" }}>
                  <Box display="flex" alignItems="baseline" gap={1} mb={1}>
                    <Typography variant="caption" fontWeight="bold">
                      Herd Streak ({hStreakTurns.length} turn{hStreakTurns.length > 1 ? "s" : ""})
                    </Typography>
                    <Chip label="HERD" size="small" sx={{ height: 16, fontSize: 9, bgcolor: "#4caf50", color: "#fff" }} />
                  </Box>

                  {hStreakTurns.map(({ turnIndex: ti, turn }) => {
                    const herd = turn.herdData!;
                    const allPids = Object.keys(herd.submissions);
                    return (
                      <Box key={ti} sx={{ mb: 1.5, pl: 1, borderLeft: "2px solid #e0e0e0" }}>
                        <Box display="flex" alignItems="baseline" gap={1} mb={0.5}>
                          <Typography variant="caption" fontWeight="bold">Turn {ti + 1}</Typography>
                          <Chip label={`${herd.streakIndex + 1}/4`} size="small" variant="outlined" sx={{ height: 16, fontSize: 9 }} />
                          {herd.wasSplit && <Chip label="SPLIT" size="small" color="warning" sx={{ height: 16, fontSize: 9 }} />}
                          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                            {turn.question.forTsar}
                          </Typography>
                        </Box>

                        {/* Options with vote counts */}
                        <Box display="flex" gap={0.5} mb={0.5} flexWrap="wrap">
                          {turn.cues.slice(0, herd.optionCounts.length).map((cue, oi) => (
                            <Box key={oi} display="flex" alignItems="center" gap={0.5}>
                              <CueChip cueIndex={oi} label={String(cue.content)} />
                              <Typography variant="caption" fontWeight="bold">
                                ({herd.optionCounts[oi] ?? 0})
                              </Typography>
                            </Box>
                          ))}
                        </Box>

                        {/* Player choices */}
                        <Box display="flex" flexDirection="column" gap={0.3}>
                          {allPids.map((pid) => {
                            const sub = herd.submissions[pid];
                            const name = pn(pid);
                            return (
                              <Box key={pid} display="flex" alignItems="center" gap={1}>
                                <Typography variant="caption" fontWeight="bold" sx={{ minWidth: 80 }}>{name}</Typography>
                                {sub.wasAlive ? (
                                  <>
                                    <Chip
                                      label={sub.choice != null ? String(turn.cues[sub.choice - 1]?.content ?? `#${sub.choice}`) : "no pick"}
                                      size="small"
                                      sx={{ height: 18, fontSize: 9, bgcolor: sub.choice != null ? (CUE_BG[sub.choice - 1] ?? "#ccc") : "#ccc", color: sub.choice != null ? (CUE_FG[sub.choice - 1] ?? "#000") : "#000" }}
                                    />
                                    <Typography variant="caption" color={sub.survived ? "success.main" : "error.main"}>
                                      {sub.survived ? "survived" : "trampled"}
                                    </Typography>
                                    {sub.points > 0 && <Typography variant="caption" color="text.secondary">+{sub.points}</Typography>}
                                  </>
                                ) : (
                                  <Typography variant="caption" color="text.disabled" sx={{ fontStyle: "italic" }}>trampled</Typography>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  })}

                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    Standings after turn {hLastTurnIndex + 1}
                  </Typography>
                  <Scoreboard players={players} cumulativeScores={cumulativeByTurn[hLastTurnIndex]} compact />
                </Paper>
              );
            }

            // ── FTL streak ──
            const { streakId, turns: streakTurns } = item;
            const firstTurn = streakTurns[0].turn;
            const czarName = pn(firstTurn.tsarId);
            const lastTurnIndex = streakTurns[streakTurns.length - 1].turnIndex;

            return (
              <Paper key={`ftl-${streakId}`} variant="outlined" sx={{ p: 1.5, mb: 1.5, borderLeft: "3px solid #1976d2" }}>
                {/* Streak header */}
                <Box display="flex" alignItems="baseline" gap={1} mb={1}>
                  <Typography variant="caption" fontWeight="bold">
                    FTL Streak ({streakTurns.length} turn{streakTurns.length > 1 ? "s" : ""})
                  </Typography>
                  <Chip label="FTL" size="small" color="primary" sx={{ height: 16, fontSize: 9 }} />
                  <Typography variant="caption" color="text.secondary">Czar: <strong>{czarName}</strong></Typography>
                </Box>

                {/* Each sub-turn in the streak */}
                {streakTurns.map(({ turnIndex: ti, turn }) => {
                  const ftl = turn.ftlData!;
                  const options = turn.cues.slice(0, ftl.optionCount);
                  const nonCzarIds = Object.keys(ftl.submissions).filter((pid) => pid !== turn.tsarId);

                  return (
                    <Box key={ti} sx={{ mb: 1.5, pl: 1, borderLeft: "2px solid #e0e0e0" }}>
                      <Box display="flex" alignItems="baseline" gap={1} mb={0.5}>
                        <Typography variant="caption" fontWeight="bold">
                          Turn {ti + 1}
                        </Typography>
                        <Chip label={`${ftl.streakIndex + 1}/5`} size="small" variant="outlined" sx={{ height: 16, fontSize: 9 }} />
                        <Chip label={`x${ftl.multiplier}`} size="small" variant="outlined" sx={{ height: 16, fontSize: 9 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                          {turn.question.forTsar}
                        </Typography>
                      </Box>

                      {/* Options with czar choice highlighted */}
                      <Box display="flex" gap={0.5} mb={0.5} flexWrap="wrap">
                        {options.map((cue, oi) => (
                          <Box key={oi} sx={{ border: ftl.czarChoice === oi + 1 ? "2px solid #333" : "2px solid transparent", borderRadius: 1 }}>
                            <CueChip cueIndex={oi} label={String(cue.content)} />
                          </Box>
                        ))}
                      </Box>

                      {/* Player choices */}
                      <Box display="flex" flexDirection="column" gap={0.3}>
                        {nonCzarIds.map((pid) => {
                          const sub = ftl.submissions[pid];
                          const name = pn(pid);
                          return (
                            <Box key={pid} display="flex" alignItems="center" gap={1}>
                              <Typography variant="caption" fontWeight="bold" sx={{ minWidth: 80 }}>{name}</Typography>
                              {sub.wasAlive ? (
                                <>
                                  <Chip
                                    label={sub.choice > 0 ? String(options[sub.choice - 1]?.content ?? `#${sub.choice}`) : "no pick"}
                                    size="small"
                                    sx={{
                                      height: 18, fontSize: 9,
                                      bgcolor: sub.choice > 0 ? (CUE_BG[sub.choice - 1] ?? "#ccc") : "#ccc",
                                      color: sub.choice > 0 ? (CUE_FG[sub.choice - 1] ?? "#000") : "#000",
                                    }}
                                  />
                                  <Typography variant="caption" color={sub.survived ? "success.main" : "error.main"}>
                                    {sub.survived ? "survived" : "eliminated"}
                                  </Typography>
                                  {sub.points > 0 && <Typography variant="caption" color="text.secondary">+{sub.points}</Typography>}
                                </>
                              ) : (
                                <Typography variant="caption" color="text.disabled" sx={{ fontStyle: "italic" }}>eliminated</Typography>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  );
                })}

                {/* Standings after the last turn in this streak */}
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                  Standings after turn {lastTurnIndex + 1}
                </Typography>
                <Scoreboard players={players} cumulativeScores={cumulativeByTurn[lastTurnIndex]} compact />
              </Paper>
            );
          })}
          {hasMore && (
            <Button size="small" onClick={() => setVisibleCount((c) => c + 10)} sx={{ mt: 1 }}>
              Load more ({reversedRendered.length - visibleCount} remaining)
            </Button>
          )}
        </Box>
      </Collapse>
    </Box>
  );
});

const LobbyDetail = React.memo(function LobbyDetail({
  lobby,
  socket,
  messageReplies,
}: {
  lobby: AdminLobbySnapshot;
  socket: Socket;
  messageReplies: { from: string; lobbyKey: string; playerId: string; text: string; ts: number }[];
}) {
  const [lobbyMsg, setLobbyMsg] = useState("");
  const [playerMsg, setPlayerMsg] = useState("");
  const [playerMsgTarget, setPlayerMsgTarget] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [kickTarget, setKickTarget] = useState<string | null>(null);

  // Tick elapsed seconds when in SUBMISSIONS phase
  useEffect(() => {
    if (lobby.gamePhase !== "R" || lobby.turnPhase !== "S" || !lobby.turnInfo) return;
    const id = setInterval(() => setElapsed(elapsedSeconds(lobby.turnInfo!.startTime)), 1000);
    return () => clearInterval(id);
  }, [lobby.gamePhase, lobby.turnPhase, lobby.turnInfo?.startTime]);

  function confirmKick() {
    if (!kickTarget) return;
    socket.emit("kickPlayer", { lobbyKey: lobby.lobbyKey, playerId: kickTarget });
    setKickTarget(null);
  }

  function forceSkip() {
    socket.emit("forceSkip", { lobbyKey: lobby.lobbyKey });
  }

  function sendLobbyMessage() {
    if (!lobbyMsg.trim()) return;
    socket.emit("sendLobbyMessage", { lobbyKey: lobby.lobbyKey, message: lobbyMsg.trim() });
    setLobbyMsg("");
  }

  function sendPlayerMessage(playerId: string) {
    if (!playerMsg.trim()) return;
    socket.emit("sendPlayerMessage", {
      lobbyKey: lobby.lobbyKey,
      playerId,
      message: playerMsg.trim(),
    });
    setPlayerMsg("");
    setPlayerMsgTarget(null);
  }

  function downloadState() {
    const blob = new Blob([JSON.stringify(lobby, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `gamestate-${lobby.lobbyKey}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const playerEntries = Object.entries(lobby.players);

  return (
    <Box p={2} overflow="auto" flex={1}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Typography variant="h6">{lobby.lobbyKey}</Typography>
        <Chip label={phaseLabel(lobby.gamePhase)} color={phaseColor(lobby.gamePhase)} size="small" />
        <Typography variant="body2" color="text.secondary">
          Round {lobby.round}/{lobby.config.roundLimit}
        </Typography>
        <Button size="small" variant="outlined" onClick={downloadState} sx={{ ml: "auto" }}>
          Download State
        </Button>
      </Box>

      {/* Game config */}
      <Box mb={1} display="flex" flexWrap="wrap" gap={0.5} alignItems="center">
        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Config:</Typography>
        <Chip label={`Timer: ${lobby.config.roundTimer}s`} size="small" variant="outlined" sx={{ fontSize: 11 }} />
        <Chip label={`Skips: ${lobby.config.maxSkips}`} size="small" variant="outlined" sx={{ fontSize: 11 }} />
        <Chip label={lobby.config.waitForHost ? "Host control" : "Ready vote"} size="small" variant="outlined" sx={{ fontSize: 11 }} />
        {lobby.config.enableJoinDuringRound && <Chip label="Join mid-round" size="small" variant="outlined" sx={{ fontSize: 11 }} />}
        {lobby.config.enabledModes && lobby.config.enabledModes.map((m) => (
          <Chip key={m} label={m} size="small" color="primary" variant="outlined" sx={{ fontSize: 11 }} />
        ))}
        {lobby.config.entryMode && (
          <Chip label={lobby.config.entryMode} size="small" color="secondary" sx={{ fontSize: 11 }} />
        )}
        {lobby.config.preset && (
          <Chip label={`Preset: ${lobby.config.preset}`} size="small" color="secondary" variant="outlined" sx={{ fontSize: 11 }} />
        )}
        {lobby.config.deckMode && (
          <Chip label={`Mode: ${lobby.config.deckMode}`} size="small" variant="outlined" sx={{ fontSize: 11 }} />
        )}
      </Box>

      {/* Selected topics */}
      {lobby.config.selectedTopics && lobby.config.selectedTopics.length > 0 && (
        <Box mb={1} display="flex" flexWrap="wrap" gap={0.5} alignItems="center">
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
            Topics:
          </Typography>
          {lobby.config.selectedTopics.map((topic) => (
            <Chip
              key={topic}
              label={topic}
              size="small"
              color="info"
              variant="outlined"
              sx={{ fontSize: 11 }}
            />
          ))}
        </Box>
      )}

      {/* Selected decks */}
      {lobby.selectedDeckIds.length > 0 && (
        <Box mb={1} display="flex" flexWrap="wrap" gap={0.5} alignItems="center">
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
            Decks:
          </Typography>
          {lobby.selectedDeckIds.map((id) => (
            <Chip
              key={id}
              label={lobby.deckNames[id] ?? id}
              size="small"
              variant="outlined"
              sx={{ fontSize: 11 }}
            />
          ))}
        </Box>
      )}

      {/* Live scoreboard */}
      {Object.keys(lobby.players).length > 0 && (
        <>
          <Typography variant="subtitle2" gutterBottom>Standings</Typography>
          <Scoreboard players={lobby.players} />
          <Divider sx={{ my: 2 }} />
        </>
      )}

      {/* Players table */}
      <Typography variant="subtitle2" gutterBottom>Players</Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Score</TableCell>
            <TableCell>Skips</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {playerEntries.map(([playerId, player]) => {
            const connected = lobby.connectedPlayerIds.includes(playerId);
            const isHost = playerId === lobby.hostId;
            const isTsar = !isVersusMode(lobby.currentTurnMode) && lobby.turnInfo?.tsarId === playerId;
            return (
              <TableRow key={playerId}>
                <TableCell>
                  {player.name}
                  {isHost && (
                    <Chip label="host" size="small" sx={{ ml: 0.5 }} color="primary" />
                  )}
                  {isTsar && (
                    <Chip
                      label="tsar"
                      size="small"
                      sx={{ ml: 0.5 }}
                      color="secondary"
                    />
                  )}
                </TableCell>
                <TableCell>{player.score}</TableCell>
                <TableCell>{isVersusMode(lobby.currentTurnMode) ? "—" : player.remainingSkips}</TableCell>
                <TableCell>
                  <Box
                    component="span"
                    sx={{
                      display: "inline-block",
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: connected ? "success.main" : "error.main",
                      mr: 0.5,
                    }}
                  />
                  {connected ? "online" : "away"}
                </TableCell>
                <TableCell>
                  <Button size="small" color="error" onClick={() => setKickTarget(playerId)}>
                    Kick
                  </Button>
                  <Button
                    size="small"
                    onClick={() =>
                      setPlayerMsgTarget(playerMsgTarget === playerId ? null : playerId)
                    }
                  >
                    Message
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {/* Per-player message input (shows inline below row's playerId) */}
      {playerMsgTarget && (
        <Box mt={1} display="flex" gap={1} alignItems="center">
          <Typography variant="body2">
            Message to {lobby.players[playerMsgTarget]?.name}:
          </Typography>
          <TextField
            size="small"
            value={playerMsg}
            onChange={(e) => setPlayerMsg(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendPlayerMessage(playerMsgTarget)}
            placeholder="Type message..."
          />
          <Button size="small" variant="contained" onClick={() => sendPlayerMessage(playerMsgTarget)}>
            Send
          </Button>
          <Button size="small" onClick={() => setPlayerMsgTarget(null)}>Cancel</Button>
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      {/* FTL State */}
      {lobby.gamePhase === "R" && lobby.ftlState && lobby.currentTurnMode === "FOLLOW_THE_LEADER" && (
        <>
          <Typography variant="subtitle2" gutterBottom>Follow the Leader</Typography>
          <Typography variant="body2">
            <strong>Streak Turn:</strong> {lobby.ftlState.streakIndex + 1} / 5
          </Typography>
          <Typography variant="body2">
            <strong>Czar:</strong> {lobby.players[lobby.ftlState.czarId]?.name ?? lobby.leftPlayers?.[lobby.ftlState.czarId] ?? lobby.ftlState.czarId}
          </Typography>
          <Typography variant="body2">
            <strong>Alive:</strong> {lobby.ftlState.aliveCount} | <strong>Dead:</strong> {lobby.ftlState.deadCount}
          </Typography>
          <Typography variant="body2">
            <strong>Multiplier:</strong> x{lobby.ftlState.multiplier}
          </Typography>
          <Divider sx={{ my: 2 }} />
        </>
      )}

      {/* Reverse Round State */}
      {lobby.gamePhase === "R" && lobby.reverseRoundState && lobby.currentTurnMode === "REVERSE_ROUND" && (
        <>
          <Typography variant="subtitle2" gutterBottom>Reverse Round</Typography>
          <Typography variant="body2">
            <strong>Stage:</strong> {lobby.reverseRoundState.stage} ({lobby.reverseRoundState.stage === 1 ? "Truthful Submission" : "Guessing"})
          </Typography>
          <Typography variant="body2">
            <strong>Submissions:</strong> {lobby.reverseRoundState.submissionCount}
          </Typography>
          {lobby.reverseRoundState.stage === 2 && (
            <Typography variant="body2">
              <strong>Guesses:</strong> {lobby.reverseRoundState.guessCount}
            </Typography>
          )}
          <Typography variant="body2">
            <strong>High Roll:</strong> {lobby.reverseRoundState.highRollEnabled ? "Yes" : "No"}
          </Typography>
          <Divider sx={{ my: 2 }} />
        </>
      )}

      {/* Herd State */}
      {lobby.gamePhase === "R" && lobby.herdState && lobby.currentTurnMode === "RUN_WITH_THE_HERD" && (
        <>
          <Typography variant="subtitle2" gutterBottom>Run with the Herd</Typography>
          <Typography variant="body2">
            <strong>Streak Turn:</strong> {lobby.herdState.streakIndex + 1} / 5
          </Typography>
          <Typography variant="body2">
            <strong>Alive:</strong> {lobby.herdState.aliveCount} | <strong>Dead:</strong> {lobby.herdState.deadCount}
          </Typography>
          <Divider sx={{ my: 2 }} />
        </>
      )}

      {/* Picasso State */}
      {lobby.gamePhase === "R" && lobby.picassoState && lobby.currentTurnMode === "PICASSO" && (
        <>
          <Typography variant="subtitle2" gutterBottom>Picasso</Typography>
          <Typography variant="body2" sx={{ fontStyle: "italic", mb: 0.5 }}>
            {lobby.picassoState.question}
          </Typography>
          <Typography variant="body2">
            <strong>Stage:</strong> {lobby.picassoState.stage === 1 ? "Drawing" : "Guessing"}
            {" | "}
            <strong>Timer:</strong> {Math.max(0, Math.round(lobby.picassoState.timer - (Date.now() - lobby.picassoState.startTime) / 1000))}s / {lobby.picassoState.timer}s
          </Typography>
          {lobby.picassoState.stage === 1 ? (
            <>
              <Typography variant="body2">
                <strong>Drawings:</strong> {lobby.picassoState.drawingCount} / {lobby.picassoState.totalPlayers}
                {lobby.picassoState.drawingSubmitters.length > 0 && (
                  <span style={{ color: "#90caf9" }}> ({lobby.picassoState.drawingSubmitters.join(", ")})</span>
                )}
              </Typography>
              {lobby.picassoState.imposterDetails.length > 0 && (
                <Box sx={{ mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary"><strong>Imposters:</strong></Typography>
                  {lobby.picassoState.imposterDetails.map((imp, i) => (
                    <Typography key={i} variant="caption" display="block" sx={{ pl: 1 }}>
                      {imp.imposterName} → {imp.targetName}
                      {" "}
                      <Chip
                        label={!imp.decided ? "pending" : imp.accepted ? "accepted" : "declined"}
                        size="small"
                        sx={{
                          height: 16, fontSize: 9,
                          bgcolor: !imp.decided ? "#ff9800" : imp.accepted ? "#4caf50" : "#f44336",
                          color: "#fff",
                        }}
                      />
                    </Typography>
                  ))}
                </Box>
              )}
            </>
          ) : (
            <>
              <Typography variant="body2">
                <strong>Votes:</strong> {lobby.picassoState.voteCount} / {lobby.picassoState.assignmentCount}
                {" "}({lobby.picassoState.challengeCount} challenges)
              </Typography>
              {lobby.picassoState.voterDetails.length > 0 && (
                <Box sx={{ mt: 0.5 }}>
                  {lobby.picassoState.voterDetails.map((v, i) => (
                    <Chip
                      key={i}
                      label={v.playerName}
                      size="small"
                      sx={{
                        height: 18, fontSize: 9, mr: 0.5, mb: 0.5,
                        bgcolor: v.voted ? "#4caf50" : "#616161",
                        color: "#fff",
                      }}
                    />
                  ))}
                </Box>
              )}
            </>
          )}
          <Divider sx={{ my: 2 }} />
        </>
      )}

      {/* Current turn */}
      {lobby.gamePhase === "R" && lobby.turnInfo && (
        <>
          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
            <Typography variant="subtitle2">Current Turn</Typography>
            <TurnModeChip mode={lobby.currentTurnMode} />
          </Box>
          <Typography variant="body2" gutterBottom>
            <strong>Question:</strong> {lobby.turnInfo.question.forTsar}
          </Typography>
          {isVersusMode(lobby.currentTurnMode) && lobby.turnInfo.versusGroupRanking?.length === 5 && (
            <Box sx={{ mb: 1, pl: 1, borderLeft: "3px solid #0097a7" }}>
              <Typography variant="body2" fontWeight="bold" color="#0097a7">
                Final group ranking
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                {lobby.turnInfo.versusGroupRanking.map((cueNumber, position) => (
                  <CueChip
                    key={position}
                    cueIndex={cueNumber - 1}
                    label={String(lobby.turnInfo!.cues[cueNumber - 1]?.content ?? cueNumber)}
                  />
                ))}
              </Box>
            </Box>
          )}
          <Typography variant="body2" gutterBottom>
            <strong>Options:</strong>{" "}
            {lobby.turnInfo.cues.map((c) => String(c.content)).join(", ")}
          </Typography>
          {lobby.turnPhase === "S" && (
            <Typography variant="body2" gutterBottom>
              <strong>Elapsed:</strong> {elapsed}s / {lobby.turnInfo.timer}s
            </Typography>
          )}

          {/* Submissions */}
          {Object.keys(lobby.turnInfo.submissions).length > 0 && (
            <>
              <Typography variant="body2" gutterBottom>
                <strong>Submissions ({Object.keys(lobby.turnInfo.submissions).length}/{playerEntries.length}):</strong>
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Player</TableCell>
                    <TableCell>Ranking</TableCell>
                    <TableCell align="right">Answer time</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(lobby.turnInfo.submissions).map(([pid, seq]) => (
                    <TableRow key={pid}>
                      <TableCell>{lobby.players[pid]?.name ?? lobby.leftPlayers?.[pid] ?? pid}</TableCell>
                      <TableCell>
                        {seq.map((idx) => {
                          const cue = lobby.turnInfo!.cues[idx - 1];
                          return cue ? String(cue.content) : idx;
                        }).join(" > ")}
                      </TableCell>
                      <TableCell align="right">
                        {formatResponseTime(
                          lobby.turnInfo!.submissionTimes?.[pid] == null
                            ? undefined
                            : lobby.turnInfo!.submissionTimes[pid] - lobby.turnInfo!.startTime
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}

          {lobby.turnPhase === "S" && (
            <Box mt={1}>
              <Button variant="contained" color="warning" size="small" onClick={forceSkip}>
                Force Skip
              </Button>
            </Box>
          )}
          <Divider sx={{ my: 2 }} />
        </>
      )}

      {/* Lobby message */}
      <Typography variant="subtitle2" gutterBottom>Send Lobby Message</Typography>
      <Box display="flex" gap={1}>
        <TextField
          size="small"
          fullWidth
          value={lobbyMsg}
          onChange={(e) => setLobbyMsg(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && sendLobbyMessage()}
          placeholder="Message to all players..."
        />
        <Button variant="contained" size="small" onClick={sendLobbyMessage}>
          Send
        </Button>
      </Box>

      {/* Player replies */}
      {messageReplies.length > 0 && (
        <Box mt={1}>
          <Typography variant="caption" color="text.secondary" fontWeight="bold">
            Replies ({messageReplies.length})
          </Typography>
          {messageReplies.slice(-10).map((r, i) => (
            <Box key={i} sx={{ mt: 0.5, p: 1, bgcolor: "action.hover", borderRadius: 1 }}>
              <Typography variant="caption" fontWeight="bold">{r.from}</Typography>
              <Typography variant="body2">{r.text}</Typography>
              <Typography variant="caption" color="text.secondary">
                {new Date(r.ts).toLocaleTimeString()}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Turn history */}
      <TurnHistorySection turnHistory={lobby.turnHistory} players={lobby.players} leftPlayers={lobby.leftPlayers} />

      {/* Result cards (for finished lobbies) */}
      {lobby.gamePhase === "F" && lobby.resultCards && lobby.resultCards.length > 0 && (
        <Box mt={2}>
          <Typography variant="subtitle2" gutterBottom>Result Cards</Typography>
          <Box display="flex" flexDirection="column" gap={1}>
            {lobby.resultCards.map((card, idx) => (
              <Paper key={idx} variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="caption" fontWeight="bold">{card.title}</Typography>
                {card.subtitle && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>{card.subtitle}</Typography>
                )}
                <Typography variant="body2" sx={{ mt: 0.5 }}>{card.body}</Typography>
              </Paper>
            ))}
          </Box>
        </Box>
      )}

      {/* Kick confirmation dialog */}
      <Dialog open={kickTarget !== null} onClose={() => setKickTarget(null)}>
        <DialogTitle>Kick player?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Kick <strong>{kickTarget ? lobby.players[kickTarget]?.name : ""}</strong> from the
            lobby? They will be disconnected immediately.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKickTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmKick}>
            Kick
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

// ────────────────────────── Feedback Panel ──────────────────────────

interface FeedbackItem {
  id: number;
  message: string;
  author: string;
  lobbyKey: string | null;
  createdAt: string;
}

function FeedbackPanel({ adminSecret }: { adminSecret: string }) {
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pageSize = 20;

  async function load(p: number) {
    setLoading(true);
    setError(null);
    try {
      const resp = await apiFetch(`/api/admin/feedback?page=${p}&pageSize=${pageSize}`, {
        headers: { "x-admin-secret": adminSecret },
      });
      if (!resp.ok) throw new Error("Failed to fetch feedback");
      const data = await resp.json();
      setItems(data.items);
      setTotal(data.total);
      setPage(p);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(1); }, []);

  const totalPages = Math.ceil(total / pageSize);

  return (
    <Box p={2} overflow="auto" flex={1}>
      <Typography variant="h6" mb={2}>Feedback ({total})</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>Author</TableCell>
            <TableCell>Lobby</TableCell>
            <TableCell>Message</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item.id}>
              <TableCell sx={{ whiteSpace: "nowrap" }}>
                {new Date(item.createdAt).toLocaleString()}
              </TableCell>
              <TableCell>{item.author || "-"}</TableCell>
              <TableCell>{item.lobbyKey || "-"}</TableCell>
              <TableCell sx={{ maxWidth: 500, wordBreak: "break-word" }}>{item.message}</TableCell>
            </TableRow>
          ))}
          {items.length === 0 && !loading && (
            <TableRow>
              <TableCell colSpan={4} align="center">
                <Typography color="text.secondary" variant="body2">No feedback yet</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {totalPages > 1 && (
        <Box display="flex" gap={1} mt={2} alignItems="center">
          <Button size="small" disabled={page <= 1} onClick={() => load(page - 1)}>Prev</Button>
          <Typography variant="body2">Page {page} / {totalPages}</Typography>
          <Button size="small" disabled={page >= totalPages} onClick={() => load(page + 1)}>Next</Button>
        </Box>
      )}
    </Box>
  );
}

// ────────────────────────── Deck Reports Panel ──────────────────────────

interface DeckReportItem {
  id: number;
  deckId: string;
  deckName: string;
  reason: string;
  message: string | null;
  reporter: string | null;
  status: string;
  createdAt: string;
}

function DeckReportsPanel({ adminSecret }: { adminSecret: string }) {
  const [items, setItems] = useState<DeckReportItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pageSize = 20;

  async function load(p: number) {
    setLoading(true);
    setError(null);
    try {
      const resp = await apiFetch(`/api/admin/deckReports?page=${p}&pageSize=${pageSize}`, {
        headers: { "x-admin-secret": adminSecret },
      });
      if (!resp.ok) throw new Error("Failed to fetch reports");
      const data = await resp.json();
      setItems(data.items);
      setTotal(data.total);
      setPage(p);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function updateStatus(id: number, status: string) {
    try {
      await apiFetch(`/api/admin/deckReports/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "x-admin-secret": adminSecret },
        body: JSON.stringify({ status }),
      });
      load(page);
    } catch (e: any) {
      setError(e.message);
    }
  }

  useEffect(() => { load(1); }, []);

  const totalPages = Math.ceil(total / pageSize);

  return (
    <Box p={2} overflow="auto" flex={1}>
      <Typography variant="h6" mb={2}>Deck Reports ({total})</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>Deck</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell>Message</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item.id}>
              <TableCell sx={{ whiteSpace: "nowrap" }}>
                {new Date(item.createdAt).toLocaleString()}
              </TableCell>
              <TableCell>{item.deckName}</TableCell>
              <TableCell>{item.reason}</TableCell>
              <TableCell sx={{ maxWidth: 300, wordBreak: "break-word" }}>{item.message || "-"}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={item.status}
                  color={item.status === "pending" ? "warning" : item.status === "action-taken" ? "error" : "default"}
                />
              </TableCell>
              <TableCell>
                {item.status === "pending" && (
                  <Box display="flex" gap={0.5}>
                    <Button size="small" onClick={() => updateStatus(item.id, "dismissed")}>Dismiss</Button>
                    <Button size="small" color="warning" onClick={() => updateStatus(item.id, "reviewed")}>Reviewed</Button>
                  </Box>
                )}
              </TableCell>
            </TableRow>
          ))}
          {items.length === 0 && !loading && (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <Typography color="text.secondary" variant="body2">No reports yet</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {totalPages > 1 && (
        <Box display="flex" gap={1} mt={2} alignItems="center">
          <Button size="small" disabled={page <= 1} onClick={() => load(page - 1)}>Prev</Button>
          <Typography variant="body2">Page {page} / {totalPages}</Typography>
          <Button size="small" disabled={page >= totalPages} onClick={() => load(page + 1)}>Next</Button>
        </Box>
      )}
    </Box>
  );
}

// ────────────────────────── Content Stats Panel ──────────────────────────

type HorizonKey = "1d" | "1w" | "1m" | "3m" | "1y";

const HORIZON_OPTIONS: { key: HorizonKey; label: string; periodLabel: string }[] = [
  { key: "1d",  label: "1 day",    periodLabel: "last 24 h" },
  { key: "1w",  label: "1 week",   periodLabel: "last 7 days" },
  { key: "1m",  label: "1 month",  periodLabel: "last 30 days" },
  { key: "3m",  label: "3 months", periodLabel: "last 3 months" },
  { key: "1y",  label: "1 year",   periodLabel: "last year" },
];

interface ContentStats {
  migrationCutoff: string | null;
  horizon: string;
  totals: { decks: number; categories: number; questions: number; answers: number };
  period: { decks: number; categories: number; questions: number; answers: number };
  recentDecks: Array<{ id: string; name: string; language: string; createdAt: string; updatedAt: string; creatorId: string; user: { name: string } }>;
  recentCategories: Array<{ name: string; deckId: string; createdAt: string; updatedAt: string; deck: { name: string; creatorId: string; user: { name: string } } }>;
  recentQuestions: Array<{ id: number; forTsar: string; createdAt: string; updatedAt: string }>;
  recentAnswers: Array<{ id: number; answer: string; type: string; createdAt: string; updatedAt: string }>;
  dailyCategories: Array<{ day: string; count: number }>;
}

function FixUnnamedDecksButton({ adminSecret }: { adminSecret: string }) {
  const [result, setResult] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function fix() {
    setLoading(true);
    setResult(null);
    try {
      const resp = await apiFetch("/api/admin/fix-unnamed-decks", {
        method: "POST",
        headers: { "x-admin-secret": adminSecret },
      });
      if (!resp.ok) throw new Error("Failed");
      const data = await resp.json();
      const parts = [];
      if (data.deleted) parts.push(`Deleted ${data.deleted} empty deck(s)`);
      if (data.fixed) parts.push(`Renamed ${data.fixed} deck(s)`);
      setResult(parts.length ? parts.join(", ") : "No unnamed decks found");
    } catch {
      setResult("Error fixing decks");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box display="flex" alignItems="center" gap={1} mb={2}>
      <Button size="small" variant="outlined" onClick={fix} disabled={loading}>
        {loading ? "Fixing..." : "Fix Unnamed Decks"}
      </Button>
      {result && <Alert severity="info" sx={{ py: 0, flex: 1 }}>{result}</Alert>}
    </Box>
  );
}

type ContentTab = "decks" | "categories" | "questions" | "answers";

function ContentStatsPanel({ adminSecret }: { adminSecret: string }) {
  const [stats, setStats] = useState<ContentStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<ContentTab>("decks");
  const [horizon, setHorizon] = useState<HorizonKey>("1w");
  const [previewDeckId, setPreviewDeckId] = useState<string | null>(null);

  async function load(h: HorizonKey) {
    setLoading(true);
    setError(null);
    try {
      const resp = await apiFetch(`/api/admin/content-stats?horizon=${h}`, {
        headers: { "x-admin-secret": adminSecret },
      });
      if (!resp.ok) throw new Error("Failed to fetch content stats");
      setStats(await resp.json());
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(horizon); }, [horizon]);

  const periodLabel = HORIZON_OPTIONS.find((o) => o.key === horizon)?.periodLabel ?? horizon;

  const tabButtons: { key: ContentTab; label: string }[] = stats ? [
    { key: "decks",      label: `Decks (${stats.totals.decks})` },
    { key: "categories", label: `Categories (${stats.totals.categories})` },
    { key: "questions",  label: `Questions (${stats.totals.questions})` },
    { key: "answers",    label: `Answers (${stats.totals.answers})` },
  ] : [];

  return (
    <Box p={2} overflow="auto" flex={1}>
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={1}>
        <Typography variant="h6">Content Overview</Typography>
        <Box display="flex" gap={0.5} ml={1} flexWrap="wrap">
          {HORIZON_OPTIONS.map((o) => (
            <Button
              key={o.key}
              size="small"
              variant={horizon === o.key ? "contained" : "outlined"}
              onClick={() => setHorizon(o.key)}
              sx={{ minWidth: 0, px: 1 }}
            >
              {o.label}
            </Button>
          ))}
        </Box>
        <Button size="small" variant="outlined" onClick={() => load(horizon)} sx={{ ml: "auto" }}>
          Refresh
        </Button>
      </Box>

      {stats?.migrationCutoff && (
        <Alert severity="info" sx={{ mb: 2, py: 0.5, fontSize: 12 }}>
          Pre-migration data excluded (records before {fmtDate(stats.migrationCutoff)} came from the schema migration and have no meaningful timestamp).
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Summary cards — show skeleton on loading, keep layout stable */}
      <Box display="flex" gap={2} flexWrap="wrap" mb={3}>
        {(["decks", "categories", "questions", "answers"] as const).map((key) => (
          <Paper key={key} variant="outlined" sx={{ p: 1.5, minWidth: 110, flex: "1 1 110px" }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "capitalize" }}>{key}</Typography>
            <Typography variant="h5" sx={{ opacity: loading ? 0.3 : 1, transition: "opacity 0.2s" }}>
              {stats ? stats.totals[key] : "-"}
            </Typography>
            <Typography variant="caption" color="success.main" sx={{ opacity: loading ? 0.3 : 1, transition: "opacity 0.2s" }}>
              +{stats ? stats.period[key] : "-"} {periodLabel}
            </Typography>
          </Paper>
        ))}
      </Box>

      <FixUnnamedDecksButton adminSecret={adminSecret} />

      {stats && (
        <>

          {/* Daily categories chart */}
          {stats.dailyCategories.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>New categories per day</Typography>
              <RResponsiveContainer width="100%" height={220}>
                <RLineChart data={stats.dailyCategories}>
                  <RCartesianGrid strokeDasharray="3 3" />
                  <RXAxis
                    dataKey="day"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v: any) => {
                      const parts = String(v).split("-");
                      return parts.length === 3 ? `${+parts[1]}/${+parts[2]}` : String(v);
                    }}
                  />
                  <RYAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <RTooltip
                    labelFormatter={(v: any) => {
                      const parts = String(v).split("-");
                      if (parts.length === 3) {
                        return new Date(+parts[0], +parts[1] - 1, +parts[2]).toLocaleDateString();
                      }
                      return String(v);
                    }}
                  />
                  <RLine type="monotone" dataKey="count" stroke="#1976d2" strokeWidth={2} dot={{ r: 3 }} name="Categories" />
                </RLineChart>
              </RResponsiveContainer>
            </Paper>
          )}

          {/* Tab switcher */}
          <Box display="flex" gap={1} mb={2} flexWrap="wrap">
            {tabButtons.map((t) => (
              <Button
                key={t.key}
                size="small"
                variant={tab === t.key ? "contained" : "outlined"}
                onClick={() => setTab(t.key)}
              >
                {t.label}
              </Button>
            ))}
          </Box>

          {/* Tables — wrapped in overflow:auto so they scroll horizontally on mobile */}
          <Box sx={{ overflowX: "auto" }}>
            {/* Decks table */}
            {tab === "decks" && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Creator</TableCell>
                    <TableCell>Lang</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Last edited</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.recentDecks.map((d) => (
                    <TableRow key={d.id}>
                      <TableCell sx={{ maxWidth: 200, wordBreak: "break-word" }}>{d.name}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{d.user?.name ?? d.creatorId}</TableCell>
                      <TableCell>{d.language}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDate(d.createdAt)}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDate(d.updatedAt)}</TableCell>
                      <TableCell>
                        <Button size="small" variant="outlined" onClick={() => setPreviewDeckId(d.id)}>
                          Preview
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {stats.recentDecks.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary">No decks yet</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}

            {/* Categories table */}
            {tab === "categories" && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Category</TableCell>
                    <TableCell>Deck</TableCell>
                    <TableCell>Creator</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Last edited</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.recentCategories.map((c, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ maxWidth: 180, wordBreak: "break-word" }}>{c.name}</TableCell>
                      <TableCell sx={{ maxWidth: 150, wordBreak: "break-word" }}>{c.deck?.name ?? c.deckId}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{c.deck?.user?.name ?? c.deck?.creatorId ?? "-"}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDate(c.createdAt)}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDate(c.updatedAt)}</TableCell>
                    </TableRow>
                  ))}
                  {stats.recentCategories.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="text.secondary">No categories yet</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}

            {/* Questions table */}
            {tab === "questions" && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Question (for Tsar)</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Last edited</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.recentQuestions.map((q) => (
                    <TableRow key={q.id}>
                      <TableCell sx={{ color: "text.secondary" }}>{q.id}</TableCell>
                      <TableCell sx={{ maxWidth: 350, wordBreak: "break-word" }}>{q.forTsar}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDate(q.createdAt)}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDate(q.updatedAt)}</TableCell>
                    </TableRow>
                  ))}
                  {stats.recentQuestions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography variant="body2" color="text.secondary">No questions yet</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}

            {/* Answers table */}
            {tab === "answers" && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Answer</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Last edited</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.recentAnswers.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell sx={{ color: "text.secondary" }}>{a.id}</TableCell>
                      <TableCell sx={{ maxWidth: 350, wordBreak: "break-word" }}>{a.answer}</TableCell>
                      <TableCell>{a.type}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDate(a.createdAt)}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDate(a.updatedAt)}</TableCell>
                    </TableRow>
                  ))}
                  {stats.recentAnswers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="text.secondary">No answers yet</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </Box>
        </>
      )}

      {/* Deck preview dialog */}
      <Dialog
        open={previewDeckId !== null}
        onClose={() => setPreviewDeckId(null)}
        fullScreen
        PaperProps={{ sx: { bgcolor: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center" } }}
      >
        <Box sx={{ position: "absolute", top: 12, right: 12, zIndex: 10 }}>
          <IconButton onClick={() => setPreviewDeckId(null)} size="small" sx={{ bgcolor: "rgba(255,255,255,0.15)" }}>
            <CloseIcon />
          </IconButton>
        </Box>
        {previewDeckId && (
          <CardDeckViewer previewDeckId={previewDeckId} setClose={() => setPreviewDeckId(null)} />
        )}
      </Dialog>
    </Box>
  );
}

// ────────────────────────── Match History Panel ──────────────────────────

interface MatchHistoryItem {
  id: number;
  lobbyKey: string;
  startedAt: string;
  finishedAt: string;
  roundsPlayed: number;
  roundLimit: number;
  roundTimer: number;
  maxSkips: number;
  waitForHost: boolean;
  joinDuringRound: boolean;
  playerCount: number;
  gamePlayers: Array<{ id: number; nameAtGameTime: string; finalScore: number; playerId: string }>;
  gameDecks: Array<{ deck: { id: string; name: string } }>;
  topics: string | null;
}

interface AnswerCardInfo { id: number; answer: string; type: string }

interface StdSubmission {
  id: number;
  rank1: number; rank2: number; rank3: number; rank4: number; rank5: number;
  points: number;
  isTsar: boolean;
  submittedAt: string;
  gamePlayer: { id: number; nameAtGameTime: string; playerId: string };
}

interface StdTurnData {
  id: number;
  cue1AnswerCard: AnswerCardInfo;
  cue2AnswerCard: AnswerCardInfo;
  cue3AnswerCard: AnswerCardInfo;
  cue4AnswerCard: AnswerCardInfo;
  cue5AnswerCard: AnswerCardInfo;
  standardSubmissions: StdSubmission[];
}

interface VersusSubmission {
  id: number;
  rank1: number; rank2: number; rank3: number; rank4: number; rank5: number;
  points: number;
  submittedAt: string;
  responseTimeMs: number;
  gamePlayer: { id: number; nameAtGameTime: string; playerId: string };
}

interface VersusTurnDataDetail {
  id: number;
  cue1AnswerCard: AnswerCardInfo;
  cue2AnswerCard: AnswerCardInfo;
  cue3AnswerCard: AnswerCardInfo;
  cue4AnswerCard: AnswerCardInfo;
  cue5AnswerCard: AnswerCardInfo;
  groupRank1: number; groupRank2: number; groupRank3: number; groupRank4: number; groupRank5: number;
  aggregationVersion: number;
  submissionCount: number;
  versusSubmissions: VersusSubmission[];
}

interface FtlSubmission {
  id: number;
  choice: number;
  wasAlive: boolean;
  survived: boolean;
  points: number;
  gamePlayer: { id: number; nameAtGameTime: string; playerId: string };
}

interface FtlTurnDataDetail {
  id: number;
  streakId: string;
  streakIndex: number;
  optionCount: number;
  option1AnswerCard: AnswerCardInfo;
  option2AnswerCard: AnswerCardInfo;
  option3AnswerCard: AnswerCardInfo | null;
  option4AnswerCard: AnswerCardInfo | null;
  czarChoice: number;
  multiplier: number;
  ftlSubmissions: FtlSubmission[];
}

interface HerdSubmission {
  id: number;
  choice: number | null;
  wasAlive: boolean;
  survived: boolean;
  points: number;
  gamePlayer: { id: number; nameAtGameTime: string; playerId: string };
}

interface HerdTurnDataDetail {
  id: number;
  streakId: string;
  streakIndex: number;
  option1AnswerCard: AnswerCardInfo;
  option2AnswerCard: AnswerCardInfo;
  option3AnswerCard: AnswerCardInfo | null;
  option4AnswerCard: AnswerCardInfo | null;
  option1Count: number;
  option2Count: number;
  option3Count: number | null;
  option4Count: number | null;
  wasSplit: boolean;
  herdSubmissions: HerdSubmission[];
}

interface PicassoSubmissionDetail {
  id: number;
  drawingSvg: string | null;
  correctGuessCount: number;
  pointsEarned: number;
  submittedAt: string;
  gamePlayer: { id: number; nameAtGameTime: string; playerId: string };
}

interface PicassoImposterDetail {
  id: number;
  accepted: boolean;
  fooledCount: number;
  imposterPoints: number;
  imposterPlayer: { id: number; nameAtGameTime: string; playerId: string };
  targetPlayer: { id: number; nameAtGameTime: string; playerId: string };
}

interface PicassoTurnDataDetail {
  id: number;
  imposterCount: number;
  picassoSubmissions: PicassoSubmissionDetail[];
  picassoImposters: PicassoImposterDetail[];
}

interface MatchTurn {
  id: number;
  turnIndex: number;
  /** Mirrors the `TurnType` enum in backend/prisma/schema.prisma — keep both in sync. */
  turnType:
    | "STANDARD"
    | "STANDARD_VERSUS"
    | "FOLLOW_THE_LEADER"
    | "REVERSE_ROUND"
    | "RUN_WITH_THE_HERD"
    | "PICASSO";
  tsarPlayerId: string | null;
  startTime: string;
  endTime: string;
  timerSeconds: number;
  skipped: boolean;
  questionCard: { id: number; forTsar: string; aboutTsar: string };
  standardTurnData: StdTurnData | null;
  versusTurnData: VersusTurnDataDetail | null;
  ftlTurnData: FtlTurnDataDetail | null;
  herdTurnData: HerdTurnDataDetail | null;
  picassoTurnData: PicassoTurnDataDetail | null;
}

interface MatchDetail extends MatchHistoryItem {
  gameTurns: MatchTurn[];
  gamePlayers: Array<{
    id: number; playerId: string; nameAtGameTime: string; finalScore: number;
    auth0Sub: string | null; joinTime: string;
  }>;
}

function MatchHistoryPanel({ adminSecret }: { adminSecret: string }) {
  const [items, setItems] = useState<MatchHistoryItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedMatchId, setSelectedMatchId] = useState<number | null>(null);
  const [matchDetail, setMatchDetail] = useState<MatchDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  async function loadMore(reset?: boolean) {
    setLoading(true);
    setError(null);
    try {
      const cursor = reset ? "" : nextCursor ? `&cursor=${nextCursor}` : "";
      const resp = await apiFetch(`/api/admin/match-history?pageSize=10${cursor}`, {
        headers: { "x-admin-secret": adminSecret },
      });
      if (!resp.ok) throw new Error("Failed to fetch match history");
      const data = await resp.json();
      setItems((prev) => reset ? data.items : [...prev, ...data.items]);
      setHasMore(data.hasMore);
      setNextCursor(data.nextCursor);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadDetail(id: number) {
    setSelectedMatchId(id);
    setDetailLoading(true);
    try {
      const resp = await apiFetch(`/api/admin/match-history/${id}`, {
        headers: { "x-admin-secret": adminSecret },
      });
      if (!resp.ok) throw new Error("Failed to fetch match detail");
      setMatchDetail(await resp.json());
    } catch (e: any) {
      setError((e as Error).message);
    } finally {
      setDetailLoading(false);
    }
  }


  useEffect(() => { loadMore(true); }, []);

  // ── List view ──
  if (selectedMatchId === null) {
    return (
      <Box p={2} overflow="auto" flex={1}>
        <Typography variant="h6" mb={2}>Match History</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Lobby</TableCell>
              <TableCell>Players</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Decks / Topics</TableCell>
              <TableCell>Winner</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((m) => {
              const dur = Math.round((new Date(m.finishedAt).getTime() - new Date(m.startedAt).getTime()) / 60000);
              const winner = m.gamePlayers[0];
              return (
                <TableRow
                  key={m.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => loadDetail(m.id)}
                >
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDate(m.finishedAt)}</TableCell>
                  <TableCell>{m.lobbyKey}</TableCell>
                  <TableCell>{m.playerCount}</TableCell>
                  <TableCell>{dur} min</TableCell>
                  <TableCell sx={{ maxWidth: 180 }}>
                    {m.topics
                      ? m.topics.split(",").join(", ")
                      : m.gameDecks.map((gd) => gd.deck.name).join(", ") || "-"}
                  </TableCell>
                  <TableCell>
                    {winner ? `${winner.nameAtGameTime} (${winner.finalScore})` : "-"}
                  </TableCell>
                </TableRow>
              );
            })}
            {items.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">No completed matches yet</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {hasMore && (
          <Box mt={2} textAlign="center">
            <Button variant="outlined" size="small" onClick={() => loadMore()} disabled={loading}>
              {loading ? "Loading..." : "Load More"}
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  // ── Detail view ──
  if (detailLoading || !matchDetail) {
    return (
      <Box p={2}>
        <Button size="small" onClick={() => { setSelectedMatchId(null); setMatchDetail(null); }}>
          Back to list
        </Button>
        <Typography mt={2} color="text.secondary">{detailLoading ? "Loading..." : "Match not found"}</Typography>
      </Box>
    );
  }

  const md = matchDetail;
  const durationMin = Math.round((new Date(md.finishedAt).getTime() - new Date(md.startedAt).getTime()) / 60000);

  // Build player lookup by playerId (session ID used in tsarPlayerId)
  const playerByPlayerId: Record<string, { name: string; gpId: number }> = {};
  const playerByGpId: Record<number, string> = {};
  for (const gp of md.gamePlayers) {
    playerByPlayerId[gp.playerId] = { name: gp.nameAtGameTime, gpId: gp.id };
    playerByGpId[gp.id] = gp.nameAtGameTime;
  }

  // Cumulative scores (from submissions across all turns)
  const cumScores: Record<number, number> = {};
  const cumByTurn: Array<Record<number, number>> = [];
  for (const turn of md.gameTurns) {
    if (turn.standardTurnData) {
      for (const sub of turn.standardTurnData.standardSubmissions) {
        cumScores[sub.gamePlayer.id] = (cumScores[sub.gamePlayer.id] ?? 0) + sub.points;
      }
    }
    if (turn.versusTurnData) {
      for (const sub of turn.versusTurnData.versusSubmissions) {
        cumScores[sub.gamePlayer.id] = (cumScores[sub.gamePlayer.id] ?? 0) + sub.points;
      }
    }
    if (turn.ftlTurnData) {
      for (const sub of turn.ftlTurnData.ftlSubmissions) {
        cumScores[sub.gamePlayer.id] = (cumScores[sub.gamePlayer.id] ?? 0) + sub.points;
      }
    }
    if (turn.herdTurnData) {
      for (const sub of turn.herdTurnData.herdSubmissions) {
        cumScores[sub.gamePlayer.id] = (cumScores[sub.gamePlayer.id] ?? 0) + sub.points;
      }
    }
    if (turn.picassoTurnData) {
      for (const sub of turn.picassoTurnData.picassoSubmissions) {
        cumScores[sub.gamePlayer.id] = (cumScores[sub.gamePlayer.id] ?? 0) + sub.pointsEarned;
      }
    }
    cumByTurn.push({ ...cumScores });
  }

  return (
    <Box p={2} overflow="auto" flex={1}>
      <Box display="flex" gap={1} mb={1}>
        <Button size="small" onClick={() => { setSelectedMatchId(null); setMatchDetail(null); }}>
          Back to list
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => window.open(`/admin/results/${md.id}`, "_blank")}
        >
          View Result Cards
        </Button>
      </Box>

      {/* Header */}
      <Box display="flex" alignItems="baseline" gap={1} mb={1} flexWrap="wrap">
        <Typography variant="h6">{md.lobbyKey}</Typography>
        <Typography variant="body2" color="text.secondary">
          {fmtDate(md.startedAt)} — {fmtDate(md.finishedAt)} ({durationMin} min)
        </Typography>
      </Box>
      <Box display="flex" gap={1} mb={2} flexWrap="wrap">
        <Chip label={`${md.roundsPlayed}/${md.roundLimit} rounds`} size="small" />
        <Chip label={`Timer: ${md.roundTimer}s`} size="small" variant="outlined" />
        <Chip label={`Max skips: ${md.maxSkips}`} size="small" variant="outlined" />
        {md.topics
          ? md.topics.split(",").map((t) => (
              <Chip key={t} label={t} size="small" variant="outlined" color="secondary" />
            ))
          : md.gameDecks.map((gd) => (
              <Chip key={gd.deck.id} label={gd.deck.name} size="small" variant="outlined" />
            ))}
      </Box>

      {/* Final scoreboard */}
      <Typography variant="subtitle2" gutterBottom>Final Standings</Typography>
      <Table size="small" sx={{ mb: 3 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ py: 0.3, fontWeight: "bold" }}>#</TableCell>
            <TableCell sx={{ py: 0.3, fontWeight: "bold" }}>Player</TableCell>
            <TableCell sx={{ py: 0.3, fontWeight: "bold" }} align="right">Score</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {md.gamePlayers.map((gp, rank) => (
            <TableRow key={gp.id}>
              <TableCell sx={{ py: 0.2, width: 24, color: "text.secondary" }}>{rank + 1}</TableCell>
              <TableCell sx={{ py: 0.2 }}>{gp.nameAtGameTime}</TableCell>
              <TableCell sx={{ py: 0.2 }} align="right">{gp.finalScore}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Turn-by-turn history */}
      <MatchTurnHistory
        turns={md.gameTurns}
        playerByPlayerId={playerByPlayerId}
        playerByGpId={playerByGpId}
        cumByTurn={cumByTurn}
        gamePlayers={md.gamePlayers}
      />
    </Box>
  );
}

function MatchTurnHistory({
  turns,
  playerByPlayerId,
  playerByGpId,
  cumByTurn,
  gamePlayers,
}: {
  turns: MatchTurn[];
  playerByPlayerId: Record<string, { name: string; gpId: number }>;
  playerByGpId: Record<number, string>;
  cumByTurn: Array<Record<number, number>>;
  gamePlayers: Array<{ id: number; nameAtGameTime: string; finalScore: number }>;
}) {
  const [open, setOpen] = useState(false);

  if (turns.length === 0) return null;

  // Group turns: consecutive same-tsar STANDARD/skipped into one box; FTL/Herd by streakId
  type RenderedItem =
    | { type: "tsarGroup"; tsarPlayerId: string | null; mode: string | undefined; entries: { idx: number; turn: MatchTurn }[] }
    | { type: "ftlStreak"; streakId: string; entries: { idx: number; turn: MatchTurn }[] }
    | { type: "herdStreak"; streakId: string; entries: { idx: number; turn: MatchTurn }[] };

  const rendered: RenderedItem[] = [];
  for (let i = 0; i < turns.length; i++) {
    const turn = turns[i];
    if (turn.turnType === "FOLLOW_THE_LEADER" && turn.ftlTurnData) {
      const last = rendered[rendered.length - 1];
      if (last && last.type === "ftlStreak" && last.streakId === turn.ftlTurnData.streakId) {
        last.entries.push({ idx: i, turn });
      } else {
        rendered.push({ type: "ftlStreak", streakId: turn.ftlTurnData.streakId, entries: [{ idx: i, turn }] });
      }
    } else if (turn.turnType === "RUN_WITH_THE_HERD" && turn.herdTurnData) {
      const last = rendered[rendered.length - 1];
      if (last && last.type === "herdStreak" && last.streakId === turn.herdTurnData.streakId) {
        last.entries.push({ idx: i, turn });
      } else {
        rendered.push({ type: "herdStreak", streakId: turn.herdTurnData.streakId, entries: [{ idx: i, turn }] });
      }
    } else {
      // Only regular co-op turns share a tsar box. Keep Versus, Picasso, and other special turns
      // independent so their visual order always follows turnIndex.
      const last = rendered[rendered.length - 1];
      const groupTsarPlayerId = turn.turnType === "STANDARD_VERSUS" ? null : turn.tsarPlayerId;
      const canJoinTsarGroup = turn.turnType === "STANDARD";
      if (
        canJoinTsarGroup &&
        last &&
        last.type === "tsarGroup" &&
        last.tsarPlayerId === groupTsarPlayerId &&
        last.mode === turn.turnType
      ) {
        last.entries.push({ idx: i, turn });
      } else {
        rendered.push({ type: "tsarGroup", tsarPlayerId: groupTsarPlayerId, mode: turn.turnType, entries: [{ idx: i, turn }] });
      }
    }
  }

  return (
    <Box>
      <Button size="small" onClick={() => setOpen((o) => !o)} variant="outlined">
        {open ? "Hide" : "Show"} Turn History ({turns.length})
      </Button>
      <Collapse in={open}>
        <Box mt={1}>
          {rendered.map((item) => {
            // ── Same-tsar group (STANDARD + skipped) ──
            if (item.type === "tsarGroup") {
              const { tsarPlayerId, mode: groupMode, entries } = item;
              const tsarName = tsarPlayerId == null
                ? null
                : (playerByPlayerId[tsarPlayerId]?.name ?? tsarPlayerId);
              const versus = isVersusMode(groupMode);
              const picasso = groupMode === "PICASSO";
              // Find last non-skipped entry for standings
              const lastPlayedEntry = [...entries].reverse().find(e => !e.turn.skipped);
              const lastIdx = lastPlayedEntry?.idx ?? entries[entries.length - 1].idx;

              return (
                <Paper
                  key={`tg-${tsarPlayerId}-${entries[0].turn.id}`}
                  variant="outlined"
                  sx={{ p: 1.5, mb: 1.5, ...(picasso ? { borderLeft: "3px solid #ff9800" } : {}) }}
                >
                  {/* Mode + optional co-op tsar header */}
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <TurnModeChip mode={groupMode} />
                    {!versus && !picasso && (
                      <Typography variant="caption" color="text.secondary">
                        Tsar: <strong>{tsarName}</strong>
                      </Typography>
                    )}
                  </Box>

                  {entries.map(({ idx: i, turn }) => {
                    if (turn.skipped) {
                      return (
                        <Box key={turn.id} display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.5, opacity: 0.6 }}>
                          <Typography variant="caption" color="text.secondary" fontWeight="bold">Turn {turn.turnIndex + 1}</Typography>
                          <TurnModeChip mode={turn.turnType} />
                          <Chip label="SKIPPED" size="small" color="warning" sx={{ height: 14, fontSize: 9 }} />
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {turn.questionCard.forTsar}
                          </Typography>
                        </Box>
                      );
                    }

                    if (turn.turnType === "STANDARD_VERSUS" && turn.versusTurnData) {
                      const versusData = turn.versusTurnData;
                      const cues = [
                        versusData.cue1AnswerCard,
                        versusData.cue2AnswerCard,
                        versusData.cue3AnswerCard,
                        versusData.cue4AnswerCard,
                        versusData.cue5AnswerCard,
                      ];
                      const groupRanking = [
                        versusData.groupRank1,
                        versusData.groupRank2,
                        versusData.groupRank3,
                        versusData.groupRank4,
                        versusData.groupRank5,
                      ];
                      const timed = versusData.versusSubmissions
                        .filter((sub) => Number.isFinite(sub.responseTimeMs))
                        .slice()
                        .sort((a, b) => a.responseTimeMs - b.responseTimeMs);
                      const averageMs = timed.length > 0
                        ? timed.reduce((sum, sub) => sum + sub.responseTimeMs, 0) / timed.length
                        : null;
                      return (
                        <Box key={turn.id} sx={{ mt: entries.length > 1 ? 1 : 0 }}>
                          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                            <Typography variant="caption" fontWeight="bold">Turn {turn.turnIndex + 1}</Typography>
                            <TurnModeChip mode={turn.turnType} />
                            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                              {turn.questionCard.forTsar}
                            </Typography>
                          </Box>
                          <Box sx={{ mb: 1, pl: 1, borderLeft: "3px solid #0097a7" }}>
                            <Typography variant="caption" fontWeight="bold" color="#0097a7">
                              Final group ranking
                            </Typography>
                            <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.3}>
                              {groupRanking.map((cueNumber, position) => (
                                <CueChip
                                  key={position}
                                  cueIndex={cueNumber - 1}
                                  label={cues[cueNumber - 1]?.answer ?? String(cueNumber)}
                                />
                              ))}
                            </Box>
                            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                              {versusData.submissionCount} valid submissions
                              {averageMs != null && ` · Average ${formatResponseTime(averageMs)}`}
                              {timed.length > 0 && ` · Fastest ${timed[0].gamePlayer.nameAtGameTime} ${formatResponseTime(timed[0].responseTimeMs)}`}
                              {timed.length > 1 && ` · Slowest ${timed[timed.length - 1].gamePlayer.nameAtGameTime} ${formatResponseTime(timed[timed.length - 1].responseTimeMs)}`}
                            </Typography>
                          </Box>
                          <Box display="flex" flexDirection="column" gap={0.5}>
                            {versusData.versusSubmissions.map((sub, submissionIndex) => {
                              const ranks = [sub.rank1, sub.rank2, sub.rank3, sub.rank4, sub.rank5];
                              return (
                                <Box key={sub.id} sx={{ mb: 0.5 }}>
                                  <Typography variant="caption" fontWeight="bold">
                                    #{submissionIndex + 1} {sub.gamePlayer.nameAtGameTime}
                                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                      {sub.points} pts · {formatResponseTime(sub.responseTimeMs)}
                                    </Typography>
                                  </Typography>
                                  <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.3}>
                                    {ranks.map((cueIdx, pos) => (
                                      <CueChip key={pos} cueIndex={cueIdx - 1} label={cues[cueIdx - 1]?.answer ?? String(cueIdx)} />
                                    ))}
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      );
                    }

                    if (turn.turnType === "STANDARD" && turn.standardTurnData) {
                      const std = turn.standardTurnData;
                      const cues = [std.cue1AnswerCard, std.cue2AnswerCard, std.cue3AnswerCard, std.cue4AnswerCard, std.cue5AnswerCard];
                      return (
                        <Box key={turn.id} sx={{ mt: entries.length > 1 ? 1 : 0 }}>
                          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                            <Typography variant="caption" fontWeight="bold">Turn {turn.turnIndex + 1}</Typography>
                            <TurnModeChip mode={turn.turnType} />
                            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>{turn.questionCard.forTsar}</Typography>
                          </Box>
                          <Box display="flex" flexDirection="column" gap={0.5}>
                            {std.standardSubmissions
                              .slice()
                              .sort((a, b) =>
                                a.isTsar === b.isTsar ? b.points - a.points : a.isTsar ? -1 : 1
                              )
                              .map((sub) => {
                                const ranks = [sub.rank1, sub.rank2, sub.rank3, sub.rank4, sub.rank5];
                                return (
                                  <Box key={sub.id} sx={{ mb: 0.5 }}>
                                    <Typography variant="caption" fontWeight="bold">
                                      {sub.gamePlayer.nameAtGameTime}
                                      {sub.isTsar && (
                                        <Chip
                                          label="tsar"
                                          size="small"
                                          color="secondary"
                                          sx={{ ml: 0.5, height: 14, fontSize: 9 }}
                                        />
                                      )}
                                      {!sub.isTsar && (
                                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                          {sub.points} pts · {formatResponseTime(
                                            new Date(sub.submittedAt).getTime() - new Date(turn.startTime).getTime()
                                          )}
                                        </Typography>
                                      )}
                                    </Typography>
                                    <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.3}>
                                      {ranks.map((cueIdx, pos) => (
                                        <CueChip key={pos} cueIndex={cueIdx - 1} label={cues[cueIdx - 1]?.answer ?? String(cueIdx)} />
                                      ))}
                                    </Box>
                                  </Box>
                                );
                              })}
                          </Box>
                        </Box>
                      );
                    }

                    if (turn.turnType === "PICASSO" && turn.picassoTurnData) {
                      const picassoData = turn.picassoTurnData;
                      return (
                        <Box key={turn.id} sx={{ mt: entries.length > 1 ? 1 : 0 }}>
                          <Box display="flex" alignItems="center" gap={1} mb={0.75}>
                            <Typography variant="caption" fontWeight="bold">Turn {turn.turnIndex + 1}</Typography>
                            <TurnModeChip mode={turn.turnType} />
                            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                              {turn.questionCard.forTsar}
                            </Typography>
                          </Box>

                          <Box display="flex" flexWrap="wrap" gap={1.5}>
                            {picassoData.picassoSubmissions.map((sub) => (
                              <Box key={sub.id} sx={{ width: 150, minWidth: 0 }}>
                                {sub.drawingSvg ? (
                                  <Box sx={{ borderRadius: 1, border: "1px solid rgba(0,0,0,0.12)", overflow: "hidden", bgcolor: "#fff" }}>
                                    <SvgDrawing svg={sub.drawingSvg} width={150} height={150} />
                                  </Box>
                                ) : (
                                  <Box
                                    sx={{
                                      width: 150,
                                      height: 150,
                                      borderRadius: 1,
                                      border: "1px dashed rgba(0,0,0,0.2)",
                                      display: "grid",
                                      placeItems: "center",
                                    }}
                                  >
                                    <Typography variant="caption" color="text.disabled">No drawing</Typography>
                                  </Box>
                                )}
                                <Typography variant="caption" fontWeight="bold" display="block" mt={0.5} noWrap>
                                  {sub.gamePlayer.nameAtGameTime}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {sub.correctGuessCount} recognized · +{sub.pointsEarned} pts
                                </Typography>
                              </Box>
                            ))}
                          </Box>

                          {picassoData.picassoImposters.length > 0 && (
                            <Box mt={1}>
                              <Typography variant="caption" fontWeight="bold" color="text.secondary">
                                Imposters
                              </Typography>
                              {picassoData.picassoImposters.map((imposter) => (
                                <Typography key={imposter.id} variant="caption" color="text.secondary" display="block">
                                  {imposter.imposterPlayer.nameAtGameTime} → {imposter.targetPlayer.nameAtGameTime}:{" "}
                                  {imposter.accepted
                                    ? (imposter.fooledCount > 0 ? `fooled ${imposter.fooledCount}` : "fooled nobody")
                                    : "declined"}{" "}
                                  (+{imposter.imposterPoints})
                                </Typography>
                              ))}
                            </Box>
                          )}
                        </Box>
                      );
                    }

                    return (
                      <Box key={turn.id} sx={{ opacity: 0.5, mt: 0.5 }}>
                        <Typography variant="caption">Turn {turn.turnIndex + 1} — {turn.turnType} (no data)</Typography>
                      </Box>
                    );
                  })}

                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    Standings after turn {turns[lastIdx].turnIndex + 1}
                  </Typography>
                  <Table size="small">
                    <TableBody>
                      {gamePlayers
                        .map((gp) => ({ name: gp.nameAtGameTime, score: cumByTurn[lastIdx]?.[gp.id] ?? 0 }))
                        .sort((a, b) => b.score - a.score)
                        .map((e, rank) => (
                          <TableRow key={rank}>
                            <TableCell sx={{ py: 0.2, width: 24, color: "text.secondary" }}>{rank + 1}</TableCell>
                            <TableCell sx={{ py: 0.2 }}>{e.name}</TableCell>
                            <TableCell sx={{ py: 0.2 }} align="right">{e.score}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </Paper>
              );
            }

            // ── Herd streak block ──
            if (item.type === "herdStreak") {
              const { streakId: hSid, entries: hEntries } = item;
              const hLastIdx = hEntries[hEntries.length - 1].idx;
              return (
                <Paper key={`herd-${hSid}`} variant="outlined" sx={{ p: 1.5, mb: 1.5, borderLeft: "3px solid #4caf50" }}>
                  <Box display="flex" alignItems="baseline" gap={1} mb={1}>
                    <Typography variant="caption" fontWeight="bold">
                      Herd Streak ({hEntries.length} turn{hEntries.length > 1 ? "s" : ""})
                    </Typography>
                    <Chip label="HERD" size="small" sx={{ height: 16, fontSize: 9, bgcolor: "#4caf50", color: "#fff" }} />
                  </Box>
                  {hEntries.map(({ idx, turn }) => {
                    const herd = turn.herdTurnData!;
                    const options = [herd.option1AnswerCard, herd.option2AnswerCard, herd.option3AnswerCard, herd.option4AnswerCard].filter((o): o is AnswerCardInfo => o != null);
                    const optionCounts = [herd.option1Count, herd.option2Count, herd.option3Count, herd.option4Count].filter((c): c is number => c != null);
                    return (
                      <Box key={turn.id} sx={{ mb: 1.5, pl: 1, borderLeft: "2px solid #e0e0e0" }}>
                        <Box display="flex" alignItems="baseline" gap={1} mb={0.5}>
                          <Typography variant="caption" fontWeight="bold">Turn {turn.turnIndex + 1}</Typography>
                          <Chip label={`${herd.streakIndex + 1}/4`} size="small" variant="outlined" sx={{ height: 16, fontSize: 9 }} />
                          {herd.wasSplit && <Chip label="SPLIT" size="small" color="warning" sx={{ height: 16, fontSize: 9 }} />}
                          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>{turn.questionCard.forTsar}</Typography>
                        </Box>
                        <Box display="flex" gap={0.5} mb={0.5} flexWrap="wrap">
                          {options.map((opt, oi) => (
                            <Box key={oi} display="flex" alignItems="center" gap={0.5}>
                              <CueChip cueIndex={oi} label={opt.answer} />
                              <Typography variant="caption" fontWeight="bold">({optionCounts[oi] ?? 0})</Typography>
                            </Box>
                          ))}
                        </Box>
                        <Box display="flex" flexDirection="column" gap={0.3}>
                          {herd.herdSubmissions.map((sub) => (
                            <Box key={sub.id} display="flex" alignItems="center" gap={1}>
                              <Typography variant="caption" fontWeight="bold" sx={{ minWidth: 80 }}>{sub.gamePlayer.nameAtGameTime}</Typography>
                              {sub.wasAlive ? (
                                <>
                                  <Chip
                                    label={sub.choice != null ? (options[sub.choice - 1]?.answer ?? `#${sub.choice}`) : "no pick"}
                                    size="small"
                                    sx={{ height: 18, fontSize: 9, bgcolor: sub.choice != null ? (CUE_BG[sub.choice - 1] ?? "#ccc") : "#ccc", color: sub.choice != null ? (CUE_FG[sub.choice - 1] ?? "#000") : "#000" }}
                                  />
                                  <Typography variant="caption" color={sub.survived ? "success.main" : "error.main"}>
                                    {sub.survived ? "survived" : "trampled"}
                                  </Typography>
                                  {sub.points > 0 && <Typography variant="caption" color="text.secondary">+{sub.points}</Typography>}
                                </>
                              ) : (
                                <Typography variant="caption" color="text.disabled" sx={{ fontStyle: "italic" }}>trampled</Typography>
                              )}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    );
                  })}
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    Standings after turn {hEntries[hEntries.length - 1].turn.turnIndex + 1}
                  </Typography>
                  <Table size="small">
                    <TableBody>
                      {gamePlayers
                        .map((gp) => ({ name: gp.nameAtGameTime, score: cumByTurn[hLastIdx]?.[gp.id] ?? 0 }))
                        .sort((a, b) => b.score - a.score)
                        .map((e, rank) => (
                          <TableRow key={rank}>
                            <TableCell sx={{ py: 0.2, width: 24, color: "text.secondary" }}>{rank + 1}</TableCell>
                            <TableCell sx={{ py: 0.2 }}>{e.name}</TableCell>
                            <TableCell sx={{ py: 0.2 }} align="right">{e.score}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </Paper>
              );
            }

            // ── FTL streak block ──
            const { streakId, entries } = item;
            const ftlCzarId = entries[0].turn.tsarPlayerId;
            const czarName = ftlCzarId == null
              ? "—"
              : (playerByPlayerId[ftlCzarId]?.name ?? ftlCzarId);
            const lastIdx = entries[entries.length - 1].idx;

            return (
              <Paper key={`ftl-${streakId}`} variant="outlined" sx={{ p: 1.5, mb: 1.5, borderLeft: "3px solid #1976d2" }}>
                {/* Streak header */}
                <Box display="flex" alignItems="baseline" gap={1} mb={1}>
                  <Typography variant="caption" fontWeight="bold">
                    FTL Streak ({entries.length} turn{entries.length > 1 ? "s" : ""})
                  </Typography>
                  <Chip label="FTL" size="small" color="primary" sx={{ height: 16, fontSize: 9 }} />
                  <Typography variant="caption" color="text.secondary">Czar: <strong>{czarName}</strong></Typography>
                </Box>

                {/* Sub-turns */}
                {entries.map(({ idx, turn }) => {
                  const ftl = turn.ftlTurnData!;
                  const options = [ftl.option1AnswerCard, ftl.option2AnswerCard, ftl.option3AnswerCard, ftl.option4AnswerCard]
                    .filter((o): o is AnswerCardInfo => o !== null)
                    .slice(0, ftl.optionCount);

                  return (
                    <Box key={turn.id} sx={{ mb: 1.5, pl: 1, borderLeft: "2px solid #e0e0e0" }}>
                      <Box display="flex" alignItems="baseline" gap={1} mb={0.5}>
                        <Typography variant="caption" fontWeight="bold">Turn {turn.turnIndex + 1}</Typography>
                        <Chip label={`${ftl.streakIndex + 1}/5`} size="small" variant="outlined" sx={{ height: 16, fontSize: 9 }} />
                        <Chip label={`x${ftl.multiplier}`} size="small" variant="outlined" sx={{ height: 16, fontSize: 9 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                          {turn.questionCard.forTsar}
                        </Typography>
                      </Box>

                      {/* Options with czar choice highlighted */}
                      <Box display="flex" gap={0.5} mb={0.5} flexWrap="wrap">
                        {options.map((opt, oi) => (
                          <Box key={oi} sx={{ border: ftl.czarChoice === oi + 1 ? "2px solid #333" : "2px solid transparent", borderRadius: 1 }}>
                            <CueChip cueIndex={oi} label={opt.answer} />
                          </Box>
                        ))}
                      </Box>

                      {/* Player choices */}
                      <Box display="flex" flexDirection="column" gap={0.3}>
                        {ftl.ftlSubmissions.map((sub) => (
                          <Box key={sub.id} display="flex" alignItems="center" gap={1}>
                            <Typography variant="caption" fontWeight="bold" sx={{ minWidth: 80 }}>
                              {sub.gamePlayer.nameAtGameTime}
                            </Typography>
                            {sub.wasAlive ? (
                              <>
                                <Chip
                                  label={sub.choice > 0 ? (options[sub.choice - 1]?.answer ?? `#${sub.choice}`) : "no pick"}
                                  size="small"
                                  sx={{
                                    height: 18, fontSize: 9,
                                    bgcolor: sub.choice > 0 ? (CUE_BG[sub.choice - 1] ?? "#ccc") : "#ccc",
                                    color: sub.choice > 0 ? (CUE_FG[sub.choice - 1] ?? "#000") : "#000",
                                  }}
                                />
                                <Typography variant="caption" color={sub.survived ? "success.main" : "error.main"}>
                                  {sub.survived ? "survived" : "eliminated"}
                                </Typography>
                                {sub.points > 0 && (
                                  <Typography variant="caption" color="text.secondary">+{sub.points}</Typography>
                                )}
                              </>
                            ) : (
                              <Typography variant="caption" color="text.disabled" sx={{ fontStyle: "italic" }}>eliminated</Typography>
                            )}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  );
                })}

                {/* Standings after the last turn in this streak */}
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                  Standings after turn {entries[entries.length - 1].turn.turnIndex + 1}
                </Typography>
                <Table size="small">
                  <TableBody>
                    {gamePlayers
                      .map((gp) => ({ name: gp.nameAtGameTime, score: cumByTurn[lastIdx]?.[gp.id] ?? 0 }))
                      .sort((a, b) => b.score - a.score)
                      .map((e, rank) => (
                        <TableRow key={rank}>
                          <TableCell sx={{ py: 0.2, width: 24, color: "text.secondary" }}>{rank + 1}</TableCell>
                          <TableCell sx={{ py: 0.2 }}>{e.name}</TableCell>
                          <TableCell sx={{ py: 0.2 }} align="right">{e.score}</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </Paper>
            );
          })}

          {/* Final standings using authoritative DB scores */}
          <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "action.hover" }}>
            <Typography variant="caption" fontWeight="bold">Final Standings</Typography>
            <Table size="small">
              <TableBody>
                {gamePlayers
                  .slice()
                  .sort((a, b) => b.finalScore - a.finalScore)
                  .map((gp, rank) => (
                    <TableRow key={gp.id}>
                      <TableCell sx={{ py: 0.2, width: 24, color: "text.secondary" }}>{rank + 1}</TableCell>
                      <TableCell sx={{ py: 0.2 }}>{gp.nameAtGameTime}</TableCell>
                      <TableCell sx={{ py: 0.2 }} align="right">{gp.finalScore}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      </Collapse>
    </Box>
  );
}

// ────────────────────────── Analytics Panel ──────────────────────────

interface EloRow {
  answerCardId: number;
  answer: string;
  type: string;
  questionCardId: number;
  questionForTsar: string;
  elo: number;
  matchCount: number;
  wins: number;
  losses: number;
  rankEntropy: number;
  rankDistribution: number[];
  winRate: number;
}

interface PcaData {
  categoryName: string;
  deckId: string;
  questionCardId: number;
  components: number[][];
  explainedVariance: number[];
  cardIds: number[];
  playerCount: number;
  cardCount: number;
  totalComparisons: number;
  lastUpdatedAt: string;
}

interface CheckpointData {
  jobName?: string;
  lastProcessedGameTurnId: number;
  lastRunAt: string;
  lastRunDurationMs: number;
  turnsProcessed: number;
  status: string;
}

interface QuestionEntry {
  questionCardId: number;
  forTsar: string;
  cardCount: number;
}

/** Tiny inline bar for rank distribution. */
function RankDistBar({ counts }: { counts: number[] }) {
  const total = counts.reduce((a, b) => a + b, 0);
  if (total === 0) return <Typography variant="caption" color="text.secondary">-</Typography>;
  return (
    <Box display="flex" height={14} width={80} borderRadius={1} overflow="hidden" border="1px solid #ccc">
      {counts.map((c, i) => {
        const pct = (c / total) * 100;
        if (pct === 0) return null;
        return (
          <Box
            key={i}
            sx={{
              width: `${pct}%`,
              bgcolor: CUE_BG[i] ?? "#ccc",
              minWidth: pct > 0 ? 2 : 0,
            }}
            title={`Rank ${i + 1}: ${c} (${Math.round(pct)}%)`}
          />
        );
      })}
    </Box>
  );
}

function AnalyticsPanel({ adminSecret }: { adminSecret: string }) {
  const [checkpoint, setCheckpoint] = useState<CheckpointData | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState<string | null>(null);
  const [fixRunning, setFixRunning] = useState(false);
  const [fixResult, setFixResult] = useState<string | null>(null);

  // Category + question browser state
  const [deckId, setDeckId] = useState("");
  const [categoryName, setCategoryName] = useState("");
  const [questions, setQuestions] = useState<QuestionEntry[]>([]);
  const [selectedQuestion, setSelectedQuestion] = useState<number | null>(null);
  const [eloRows, setEloRows] = useState<EloRow[]>([]);
  const [pcaData, setPcaData] = useState<PcaData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const headers = { "x-admin-secret": adminSecret };

  async function loadStatus() {
    setStatusLoading(true);
    try {
      const resp = await apiFetch("/api/admin/analytics/status", { headers });
      if (!resp.ok) throw new Error("Failed to fetch status");
      const data = await resp.json();
      setCheckpoint(data.status === "never_run" ? null : data);
    } catch {
      setCheckpoint(null);
    } finally {
      setStatusLoading(false);
    }
  }

  async function triggerRun() {
    setRunning(true);
    setRunResult(null);
    try {
      const resp = await apiFetch("/api/admin/analytics/run", { method: "POST", headers });
      if (!resp.ok) throw new Error("Run failed");
      const data = await resp.json();
      setRunResult(
        `Done: ${data.turnsProcessed} turns, ${data.eloUpserts} Elo upserts, ${data.pcaGroups} PCA groups`
      );
      loadStatus();
    } catch (e: any) {
      setRunResult("Error: " + e.message);
    } finally {
      setRunning(false);
    }
  }

  async function fixLeastToMost() {
    if (!window.confirm("This will flip all 'least to most' questions to 'most to least', reverse affected sequences, and recompute all analytics from scratch. Continue?")) return;
    setFixRunning(true);
    setFixResult(null);
    try {
      const resp = await apiFetch("/api/admin/analytics/fix-least-to-most", { method: "POST", headers });
      if (!resp.ok) throw new Error("Migration failed");
      const data = await resp.json();
      setFixResult(
        `Done: ${data.questionsFixed} questions fixed, ${data.standardReversed} standard + ${data.versusReversed ?? 0} Versus + ${data.rrReversed} RR submissions reversed. Analytics: ${data.analytics.turnsProcessed} turns, ${data.analytics.eloUpserts} Elo upserts.`
      );
      loadStatus();
    } catch (e: any) {
      setFixResult("Error: " + e.message);
    } finally {
      setFixRunning(false);
    }
  }

  async function loadQuestions() {
    if (!deckId.trim() || !categoryName.trim()) return;
    setLoading(true);
    setError(null);
    setQuestions([]);
    setSelectedQuestion(null);
    setEloRows([]);
    setPcaData(null);
    try {
      const resp = await apiFetch(
        `/api/admin/analytics/questions/${encodeURIComponent(deckId.trim())}/${encodeURIComponent(categoryName.trim())}`,
        { headers }
      );
      if (!resp.ok) throw new Error("Failed to fetch");
      const data = await resp.json();
      if (data.length === 0) {
        setError("No analytics data for this category");
      } else {
        setQuestions(data);
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadQuestion(qId: number) {
    setSelectedQuestion(qId);
    setLoading(true);
    setEloRows([]);
    setPcaData(null);
    setError(null);
    try {
      const base = `/api/admin/analytics`;
      const d = encodeURIComponent(deckId.trim());
      const c = encodeURIComponent(categoryName.trim());
      const [eloResp, pcaResp] = await Promise.all([
        apiFetch(`${base}/elo/${d}/${c}/${qId}`, { headers }),
        apiFetch(`${base}/pca/${d}/${c}/${qId}`, { headers }),
      ]);
      if (eloResp.ok) setEloRows(await eloResp.json());
      if (pcaResp.ok) setPcaData(await pcaResp.json());
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadStatus(); }, []);

  return (
    <Box p={2} overflow="auto" flex={1}>
      <Typography variant="h6" mb={2}>Analytics</Typography>

      {/* ── Cron Status ── */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" mb={1}>
          <Typography variant="subtitle1" fontWeight="bold">Cron Status</Typography>
          <Button size="small" variant="outlined" onClick={loadStatus} disabled={statusLoading}>
            Refresh
          </Button>
          <Button size="small" variant="contained" color="warning" onClick={triggerRun} disabled={running || fixRunning}>
            {running ? "Running..." : "Trigger Run"}
          </Button>
          <Button size="small" variant="contained" color="error" onClick={fixLeastToMost} disabled={fixRunning || running}>
            {fixRunning ? "Fixing..." : "Fix least→most"}
          </Button>
        </Box>
        {runResult && (
          <Alert severity={runResult.startsWith("Error") ? "error" : "success"} sx={{ mb: 1 }}>
            {runResult}
          </Alert>
        )}
        {fixResult && (
          <Alert severity={fixResult.startsWith("Error") ? "error" : "success"} sx={{ mb: 1 }}>
            {fixResult}
          </Alert>
        )}
        {checkpoint ? (
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", width: 180 }}>Status</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={checkpoint.status}
                    color={
                      checkpoint.status === "idle" ? "success"
                        : checkpoint.status === "running" ? "primary"
                        : "error"
                    }
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Last Run</TableCell>
                <TableCell>{fmtDate(checkpoint.lastRunAt)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Duration</TableCell>
                <TableCell>{(checkpoint.lastRunDurationMs / 1000).toFixed(1)}s</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Turns Processed</TableCell>
                <TableCell>{checkpoint.turnsProcessed}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Last Turn ID</TableCell>
                <TableCell>{checkpoint.lastProcessedGameTurnId}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {statusLoading ? "Loading..." : "Never run yet. Trigger a run to process historical data."}
          </Typography>
        )}
      </Paper>

      {/* ── Category + Question Browser ── */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold" mb={1}>Category + Question Browser</Typography>
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap" mb={2}>
          <TextField
            label="Deck ID"
            value={deckId}
            onChange={(e) => setDeckId(e.target.value)}
            size="small"
            sx={{ width: 280 }}
          />
          <TextField
            label="Category Name"
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
            size="small"
            sx={{ width: 220 }}
            onKeyDown={(e) => e.key === "Enter" && loadQuestions()}
          />
          <Button size="small" variant="contained" onClick={loadQuestions} disabled={loading}>
            {loading && questions.length === 0 ? "Loading..." : "Load"}
          </Button>
        </Box>
        {error && <Alert severity="info" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Question picker */}
        {questions.length > 0 && (
          <Box mb={2}>
            <Typography variant="subtitle2" color="text.secondary" mb={0.5}>
              Questions with data ({questions.length}):
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={0.5}>
              {questions.map((q) => (
                <Chip
                  key={q.questionCardId}
                  label={`${q.forTsar} (${q.cardCount} cards)`}
                  size="small"
                  variant={selectedQuestion === q.questionCardId ? "filled" : "outlined"}
                  color={selectedQuestion === q.questionCardId ? "primary" : "default"}
                  onClick={() => loadQuestion(q.questionCardId)}
                  sx={{ maxWidth: 400, fontSize: 11 }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Elo Leaderboard */}
        {eloRows.length > 0 && (
          <Box mb={2}>
            <Typography variant="subtitle2" fontWeight="bold" mb={1}>
              Elo Leaderboard ({eloRows.length} cards)
              {eloRows[0]?.questionForTsar && (
                <Typography component="span" variant="caption" color="text.secondary" ml={1}>
                  — "{eloRows[0].questionForTsar}"
                </Typography>
              )}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>#</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Card</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="right">Elo</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="right">W/L</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="right">Win%</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="center">Entropy</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="center">Rank Dist</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {eloRows.map((row, i) => (
                  <TableRow key={row.answerCardId}>
                    <TableCell sx={{ color: "text.secondary" }}>{i + 1}</TableCell>
                    <TableCell sx={{ maxWidth: 200, wordBreak: "break-word" }}>
                      {row.answer}
                      <Typography variant="caption" color="text.secondary" display="block">
                        ID: {row.answerCardId}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: "bold", fontFamily: "monospace" }}>
                      {row.elo}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: "monospace" }}>
                      {row.wins}/{row.losses}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: "monospace" }}>
                      {(row.winRate * 100).toFixed(1)}%
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "inline-block",
                          px: 0.8,
                          py: 0.2,
                          borderRadius: 1,
                          fontFamily: "monospace",
                          fontSize: 12,
                          fontWeight: "bold",
                          bgcolor:
                            row.rankEntropy > 0.8 ? "#ffebee"
                              : row.rankEntropy > 0.5 ? "#fff8e1"
                              : "#e8f5e9",
                          color:
                            row.rankEntropy > 0.8 ? "#c62828"
                              : row.rankEntropy > 0.5 ? "#f57f17"
                              : "#2e7d32",
                        }}
                        title={`${row.rankEntropy.toFixed(3)} — ${row.rankEntropy > 0.8 ? "Divisive" : row.rankEntropy > 0.5 ? "Mixed" : "Consensus"}`}
                      >
                        {row.rankEntropy.toFixed(2)}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <RankDistBar counts={row.rankDistribution} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {/* PCA Summary */}
        {pcaData && (
          <Box>
            <Typography variant="subtitle2" fontWeight="bold" mb={1}>
              PCA — {pcaData.components.length} components, {pcaData.playerCount} players, {pcaData.cardCount} cards
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Component</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="right">Explained Var</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="right">Cumulative</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Top Loadings</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pcaData.explainedVariance.map((v: number, ci: number) => {
                  const cumulative = pcaData!.explainedVariance.slice(0, ci + 1).reduce((a: number, b: number) => a + b, 0);
                  const loadings = pcaData!.components[ci];
                  const indexed = loadings.map((l: number, j: number) => ({ cardId: pcaData!.cardIds[j], loading: l }));
                  indexed.sort((a: any, b: any) => Math.abs(b.loading) - Math.abs(a.loading));
                  const top3 = indexed.slice(0, 3);
                  const cardName = (id: number) => {
                    const found = eloRows.find((r) => r.answerCardId === id);
                    return found ? found.answer : `#${id}`;
                  };

                  return (
                    <TableRow key={ci}>
                      <TableCell>PC{ci + 1}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: "monospace" }}>
                        {(v * 100).toFixed(1)}%
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: "monospace" }}>
                        {(cumulative * 100).toFixed(1)}%
                      </TableCell>
                      <TableCell sx={{ fontSize: 11 }}>
                        {top3.map((t: any) => (
                          <Chip
                            key={t.cardId}
                            size="small"
                            label={`${cardName(t.cardId)} (${t.loading > 0 ? "+" : ""}${t.loading.toFixed(2)})`}
                            sx={{ mr: 0.5, mb: 0.3, fontSize: 10 }}
                            variant="outlined"
                            color={t.loading > 0 ? "success" : "error"}
                          />
                        ))}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <Typography variant="caption" color="text.secondary" mt={1} display="block">
              Total comparisons: {pcaData.totalComparisons} | Last updated: {fmtDate(pcaData.lastUpdatedAt)}
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
}

// ────────────────────────── Intermissions ──────────────────────────

const BLANK_INTERMISSION: IntermissionInput = {
  title: "",
  body: "",
  buttonLabel: "OK",
  active: true,
  priority: 0,
  minPlayers: 1,
  maxPlayers: 99,
  languages: "",
  everyRounds: 2,
  afterMatches: null,
  oncePerLobby: true,
};

/**
 * Admin messages pushed into running games at a round boundary. Every connected player has to
 * press the button before play resumes, so an intermission is the one screen the whole lobby is
 * guaranteed to read — use sparingly.
 *
 * Every targeting field is a filter that must pass, so leaving one at its default widens the
 * audience rather than narrowing it.
 */
function IntermissionsPanel({ adminSecret }: { adminSecret: string }) {
  const [items, setItems] = useState<Intermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<IntermissionInput>(BLANK_INTERMISSION);
  // undefined = leave the stored image alone, null = clear it, string = replace with this one.
  const [imageData, setImageData] = useState<string | null | undefined>(undefined);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setItems(await CommunityApi.getIntermissions(adminSecret));
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditingId(null);
    setForm(BLANK_INTERMISSION);
    setImageData(undefined);
    setImagePreview(null);
    setError("");
    setDialogOpen(true);
  };

  const openEdit = (it: Intermission) => {
    setEditingId(it.id);
    setForm({
      title: it.title,
      body: it.body,
      buttonLabel: it.buttonLabel,
      active: it.active,
      priority: it.priority,
      minPlayers: it.minPlayers,
      maxPlayers: it.maxPlayers,
      languages: it.languages,
      everyRounds: it.everyRounds,
      afterMatches: it.afterMatches,
      oncePerLobby: it.oncePerLobby,
    });
    setImageData(undefined);
    setImagePreview(it.hasImage ? serverUrl(`/api/intermissions/${it.id}/image?t=${Date.now()}`) : null);
    setError("");
    setDialogOpen(true);
  };

  const onPickFile = (file: File | undefined) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      setError("Image is larger than 8 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      setImageData(result);
      setImagePreview(result);
      setError("");
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      setError("Title and body are required");
      return;
    }
    setSaving(true);
    setError("");
    const payload = { ...form, ...(imageData !== undefined ? { image: imageData } : {}) };
    const saved = editingId
      ? await CommunityApi.updateIntermission(adminSecret, editingId, payload)
      : await CommunityApi.createIntermission(adminSecret, payload);
    setSaving(false);
    if (!saved) {
      setError("Save failed — check the image size and try again");
      return;
    }
    setDialogOpen(false);
    load();
  };

  const handleToggle = async (it: Intermission) => {
    await CommunityApi.updateIntermission(adminSecret, it.id, { active: !it.active });
    load();
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this intermission?")) return;
    await CommunityApi.deleteIntermission(adminSecret, id);
    load();
  };

  /** One-line plain-English summary of who this fires for, so the table is readable at a glance. */
  const describeTargeting = (it: Intermission) => {
    const parts: string[] = [];
    parts.push(
      it.minPlayers <= 1 && it.maxPlayers >= 99
        ? "any size"
        : `${it.minPlayers}–${it.maxPlayers} players`
    );
    parts.push(it.languages ? it.languages.toUpperCase() : "any language");
    if (it.everyRounds) parts.push(`every ${it.everyRounds} rounds`);
    if (it.afterMatches != null) parts.push(`after ${it.afterMatches} match${it.afterMatches === 1 ? "" : "es"}`);
    parts.push(it.oncePerLobby ? "once per lobby" : "repeats");
    return parts.join(" · ");
  };

  return (
    <Box p={2}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="h6">Intermissions</Typography>
        <Button variant="contained" size="small" onClick={openNew}>New</Button>
      </Box>
      <Typography variant="caption" color="text.secondary" display="block" mb={2}>
        Shown between two turns at a round boundary. Every connected player must press the button
        before the game continues. Highest priority wins when several match the same lobby.
      </Typography>

      {loading ? (
        <Typography color="text.secondary">Loading...</Typography>
      ) : items.length === 0 ? (
        <Typography color="text.secondary">No intermissions</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Message</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Targeting</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Active</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Prio</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((it) => (
              <TableRow key={it.id}>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    {it.hasImage && (
                      <Box
                        component="img"
                        src={serverUrl(`/api/intermissions/${it.id}/image`)}
                        alt=""
                        sx={{ width: 44, height: 44, objectFit: "cover", borderRadius: 1, flexShrink: 0 }}
                      />
                    )}
                    <Box minWidth={0}>
                      <Typography variant="body2" fontWeight="bold">{it.title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {it.body.substring(0, 70)}{it.body.length > 70 ? "…" : ""}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {describeTargeting(it)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Switch checked={it.active} size="small" onChange={() => handleToggle(it)} />
                </TableCell>
                <TableCell>{it.priority}</TableCell>
                <TableCell>
                  <Button size="small" onClick={() => openEdit(it)}>Edit</Button>
                  <Button size="small" color="error" onClick={() => handleDelete(it.id)}>Delete</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? "Edit intermission" : "New intermission"}</DialogTitle>
        <DialogContent>
          <TextField
            label="Title" fullWidth size="small" sx={{ mt: 1, mb: 2 }}
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
          <TextField
            label="Message" fullWidth multiline minRows={3} size="small" sx={{ mb: 2 }}
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
          />
          <TextField
            label="Button label" fullWidth size="small" sx={{ mb: 2 }}
            value={form.buttonLabel}
            onChange={(e) => setForm({ ...form, buttonLabel: e.target.value })}
          />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>Image / GIF</Typography>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            {imagePreview ? (
              <Box
                component="img" src={imagePreview} alt=""
                sx={{ maxWidth: 160, maxHeight: 110, borderRadius: 1, border: "1px solid rgba(0,0,0,0.12)" }}
              />
            ) : (
              <Typography variant="caption" color="text.secondary">No image</Typography>
            )}
            <Box>
              <Button variant="outlined" size="small" component="label">
                Choose file
                <input
                  hidden type="file"
                  accept="image/gif,image/png,image/jpeg,image/webp"
                  onChange={(e) => onPickFile(e.target.files?.[0])}
                />
              </Button>
              {imagePreview && (
                <Button
                  size="small" color="error"
                  onClick={() => { setImageData(null); setImagePreview(null); }}
                >
                  Remove
                </Button>
              )}
              <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                GIF / PNG / JPEG / WebP, max 8 MB. Stored in the database, so it survives deploys.
              </Typography>
            </Box>
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>Who sees it</Typography>
          <Box display="flex" gap={1} mb={2}>
            <TextField
              label="Min players" type="number" size="small" fullWidth
              value={form.minPlayers}
              onChange={(e) => setForm({ ...form, minPlayers: Number(e.target.value) })}
            />
            <TextField
              label="Max players" type="number" size="small" fullWidth
              value={form.maxPlayers}
              onChange={(e) => setForm({ ...form, maxPlayers: Number(e.target.value) })}
            />
          </Box>
          <TextField
            label="Languages" fullWidth size="small" sx={{ mb: 2 }}
            placeholder="en,de — leave empty for all"
            helperText="Comma-separated 2-letter codes. The lobby's language is whoever created it."
            value={form.languages}
            onChange={(e) => setForm({ ...form, languages: e.target.value })}
          />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>When it fires</Typography>
          <Box display="flex" gap={1} mb={1}>
            <TextField
              label="Every N rounds" type="number" size="small" fullWidth
              placeholder="empty = any boundary"
              value={form.everyRounds ?? ""}
              onChange={(e) => setForm({ ...form, everyRounds: e.target.value === "" ? null : Number(e.target.value) })}
            />
            <TextField
              label="After N matches" type="number" size="small" fullWidth
              placeholder="empty = from match 1"
              value={form.afterMatches ?? ""}
              onChange={(e) => setForm({ ...form, afterMatches: e.target.value === "" ? null : Number(e.target.value) })}
            />
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" mb={2}>
            "Every 2 rounds" first fires at the start of round 3. "After 1 match" means the lobby
            has to have finished a full match first. Both are filters — set both and both must hold.
          </Typography>

          <Box display="flex" alignItems="center" gap={2} mb={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.oncePerLobby}
                  onChange={(e) => setForm({ ...form, oncePerLobby: e.target.checked })}
                />
              }
              label="Once per lobby"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                />
              }
              label="Active"
            />
            <TextField
              label="Priority" type="number" size="small" sx={{ width: 110 }}
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })}
            />
          </Box>

          {error && (
            <Typography variant="caption" color="error" display="block" mt={1}>{error}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ────────────────────────── Announcements ──────────────────────────

function AnnouncementsPanel({ adminSecret }: { adminSecret: string }) {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newBody, setNewBody] = useState("");
  const [newPriority, setNewPriority] = useState(0);

  const load = async () => {
    setLoading(true);
    const data = await CommunityApi.getAdminAnnouncements(adminSecret);
    setAnnouncements(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!newTitle.trim() || !newBody.trim()) return;
    await CommunityApi.createAnnouncement(adminSecret, newTitle, newBody, newPriority);
    setNewTitle("");
    setNewBody("");
    setNewPriority(0);
    setDialogOpen(false);
    load();
  };

  const handleToggle = async (id: number, active: boolean) => {
    await CommunityApi.updateAnnouncement(adminSecret, id, { active: !active });
    load();
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this announcement?")) return;
    await CommunityApi.deleteAnnouncement(adminSecret, id);
    load();
  };

  return (
    <Box p={2}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6">Announcements</Typography>
        <Button variant="contained" size="small" onClick={() => setDialogOpen(true)}>
          New
        </Button>
      </Box>

      {loading ? (
        <Typography color="text.secondary">Loading...</Typography>
      ) : announcements.length === 0 ? (
        <Typography color="text.secondary">No announcements</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Title</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Active</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Priority</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {announcements.map((a) => (
              <TableRow key={a.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">{a.title}</Typography>
                  <Typography variant="caption" color="text.secondary">{a.body.substring(0, 80)}{a.body.length > 80 ? "..." : ""}</Typography>
                </TableCell>
                <TableCell>
                  <Switch
                    checked={a.active}
                    size="small"
                    onChange={() => handleToggle(a.id, a.active)}
                  />
                </TableCell>
                <TableCell>{a.priority}</TableCell>
                <TableCell>
                  <Button size="small" color="error" onClick={() => handleDelete(a.id)}>
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Announcement</DialogTitle>
        <DialogContent>
          <TextField
            label="Title"
            fullWidth
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
            size="small"
          />
          <TextField
            label="Body"
            fullWidth
            multiline
            rows={4}
            value={newBody}
            onChange={(e) => setNewBody(e.target.value)}
            sx={{ mb: 2 }}
            size="small"
          />
          <TextField
            label="Priority"
            type="number"
            fullWidth
            value={newPriority}
            onChange={(e) => setNewPriority(parseInt(e.target.value) || 0)}
            size="small"
            helperText="Higher priority = shown first"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate}>Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ────────────────────────── CTA Cards ──────────────────────────

function TikTokIconAdmin({ size = 24 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5
               2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01
               a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34
               6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.75a4.85 4.85 0 0 1-1.01-.06z"/>
    </svg>
  );
}

const CTA_ICON_OPTIONS: { key: string; label: string; icon: React.ReactNode }[] = [
  { key: "style",          label: "Deck Creator",  icon: <StyleIcon /> },
  { key: "people",         label: "Community",     icon: <PeopleIcon /> },
  { key: "tiktok",         label: "TikTok",        icon: <TikTokIconAdmin /> },
  { key: "star",           label: "Featured",      icon: <StarIcon /> },
  { key: "emoji_events",   label: "Achievement",   icon: <EmojiEventsIcon /> },
  { key: "favorite",       label: "Favorite",      icon: <FavoriteIcon /> },
  { key: "campaign",       label: "Announcement",  icon: <CampaignIcon /> },
  { key: "open_in_new",    label: "External Link", icon: <OpenInNewIcon /> },
  { key: "sports_esports", label: "Gaming",        icon: <SportsEsportsIcon /> },
  { key: "share",          label: "Share",         icon: <ShareIcon /> },
];

const EMPTY_CTA_FORM = { icon: "style", title: "", body: "", link: "", sortOrder: 0, enabled: true };

function CtaCardsPanel({ adminSecret }: { adminSecret: string }) {
  const [cards, setCards] = useState<CtaCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState({ ...EMPTY_CTA_FORM });

  const load = async () => {
    setLoading(true);
    const data = await CommunityApi.getAdminCtaCards(adminSecret);
    setCards(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY_CTA_FORM });
    setDialogOpen(true);
  };

  const openEdit = (card: CtaCard) => {
    setEditingId(card.id);
    setForm({ icon: card.icon, title: card.title, body: card.body, link: card.link, sortOrder: card.sortOrder, enabled: card.enabled });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.body.trim() || !form.link.trim()) return;
    if (editingId !== null) {
      await CommunityApi.updateCtaCard(adminSecret, editingId, form);
    } else {
      await CommunityApi.createCtaCard(adminSecret, form);
    }
    setDialogOpen(false);
    load();
  };

  const handleToggle = async (card: CtaCard) => {
    await CommunityApi.updateCtaCard(adminSecret, card.id, { enabled: !card.enabled });
    load();
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this CTA card?")) return;
    await CommunityApi.deleteCtaCard(adminSecret, id);
    load();
  };

  return (
    <Box p={2}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6">CTA Cards</Typography>
        <Button variant="contained" size="small" onClick={openCreate}>New Card</Button>
      </Box>

      {loading ? (
        <Typography color="text.secondary">Loading...</Typography>
      ) : cards.length === 0 ? (
        <Typography color="text.secondary">No CTA cards</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Icon</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Title / Body</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Link</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Order</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Enabled</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cards.map((card) => {
              const iconOption = CTA_ICON_OPTIONS.find((o) => o.key === card.icon);
              return (
                <TableRow key={card.id}>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", color: "#ec7777" }}>
                      {iconOption ? iconOption.icon : <StyleIcon />}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">{card.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {card.body.length > 60 ? card.body.substring(0, 60) + "…" : card.body}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {card.link.length > 40 ? card.link.substring(0, 40) + "…" : card.link}
                    </Typography>
                  </TableCell>
                  <TableCell>{card.sortOrder}</TableCell>
                  <TableCell>
                    <Switch checked={card.enabled} size="small" onChange={() => handleToggle(card)} />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => openEdit(card)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(card.id)}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId !== null ? "Edit CTA Card" : "New CTA Card"}</DialogTitle>
        <DialogContent>
          {/* Icon picker: 2×5 grid */}
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, mt: 1 }}>Icon</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 1, mb: 2 }}>
            {CTA_ICON_OPTIONS.map((opt) => (
              <Box
                key={opt.key}
                onClick={() => setForm((f) => ({ ...f, icon: opt.key }))}
                sx={{
                  display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25,
                  p: 1, borderRadius: 1, cursor: "pointer", border: "2px solid",
                  borderColor: form.icon === opt.key ? "#ec7777" : "transparent",
                  bgcolor: form.icon === opt.key ? "action.selected" : "action.hover",
                  "&:hover": { bgcolor: "action.selected" },
                  color: form.icon === opt.key ? "#ec7777" : "text.secondary",
                }}
              >
                {opt.icon}
                <Typography variant="caption" sx={{ fontSize: "0.6rem", textAlign: "center", lineHeight: 1.2 }}>{opt.label}</Typography>
              </Box>
            ))}
          </Box>
          <TextField
            label="Title"
            fullWidth
            required
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            sx={{ mb: 2 }}
            size="small"
            inputProps={{ maxLength: 255 }}
          />
          <TextField
            label="Body"
            fullWidth
            required
            multiline
            rows={2}
            value={form.body}
            onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
            sx={{ mb: 2 }}
            size="small"
            inputProps={{ maxLength: 500 }}
          />
          <TextField
            label="Link"
            fullWidth
            required
            value={form.link}
            onChange={(e) => setForm((f) => ({ ...f, link: e.target.value }))}
            sx={{ mb: 2 }}
            size="small"
            helperText='Internal: /community — External: https://...'
            inputProps={{ maxLength: 500 }}
          />
          <Box display="flex" gap={2} alignItems="center">
            <TextField
              label="Sort Order"
              type="number"
              value={form.sortOrder}
              onChange={(e) => setForm((f) => ({ ...f, sortOrder: parseInt(e.target.value) || 0 }))}
              size="small"
              sx={{ width: 120 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.enabled}
                  onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                  size="small"
                />
              }
              label="Enabled"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.title.trim() || !form.body.trim() || !form.link.trim()}>
            {editingId !== null ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ────────────────────────── Social Links Panel ──────────────────────────

const EMPTY_SOCIAL_FORM = { label: "", url: "", svgMarkup: "", sortOrder: 0, enabled: true };

function SocialLinksPanel({ adminSecret }: { adminSecret: string }) {
  const [links, setLinks] = useState<SocialLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState({ ...EMPTY_SOCIAL_FORM });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    setLoading(true);
    const data = await CommunityApi.getAdminSocialLinks(adminSecret);
    setLinks(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY_SOCIAL_FORM });
    setDialogOpen(true);
  };

  const openEdit = (link: SocialLink) => {
    setEditingId(link.id);
    setForm({ label: link.label, url: link.url, svgMarkup: link.svgMarkup, sortOrder: link.sortOrder, enabled: link.enabled });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.label.trim() || !form.url.trim() || !form.svgMarkup.trim()) return;
    if (editingId !== null) {
      await CommunityApi.updateSocialLink(adminSecret, editingId, form);
    } else {
      await CommunityApi.createSocialLink(adminSecret, form);
    }
    setDialogOpen(false);
    load();
  };

  const handleToggle = async (link: SocialLink) => {
    await CommunityApi.updateSocialLink(adminSecret, link.id, { enabled: !link.enabled });
    load();
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this social link?")) return;
    await CommunityApi.deleteSocialLink(adminSecret, id);
    load();
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = reader.result as string;
      setForm((f) => ({ ...f, svgMarkup: text }));
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  return (
    <Box p={2}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6">Social Links</Typography>
        <Button variant="contained" size="small" onClick={openCreate}>New Link</Button>
      </Box>

      {loading ? (
        <Typography color="text.secondary">Loading...</Typography>
      ) : links.length === 0 ? (
        <Typography color="text.secondary">No social links</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Icon</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Label</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>URL</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Order</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Enabled</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {links.map((link) => (
              <TableRow key={link.id}>
                <TableCell>
                  <Box
                    sx={{ width: 24, height: 24, color: "#ec7777", "& svg": { width: 24, height: 24 } }}
                    dangerouslySetInnerHTML={{ __html: link.svgMarkup }}
                  />
                </TableCell>
                <TableCell>{link.label}</TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {link.url.length > 40 ? link.url.substring(0, 40) + "…" : link.url}
                  </Typography>
                </TableCell>
                <TableCell>{link.sortOrder}</TableCell>
                <TableCell>
                  <Switch checked={link.enabled} size="small" onChange={() => handleToggle(link)} />
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => openEdit(link)}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(link.id)}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId !== null ? "Edit Social Link" : "New Social Link"}</DialogTitle>
        <DialogContent>
          <TextField
            label="Label"
            fullWidth
            required
            value={form.label}
            onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
            sx={{ mb: 2, mt: 1 }}
            size="small"
            placeholder="TikTok"
            inputProps={{ maxLength: 100 }}
          />
          <TextField
            label="URL"
            fullWidth
            required
            value={form.url}
            onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
            sx={{ mb: 2 }}
            size="small"
            placeholder="https://tiktok.com/@yourhandle"
            inputProps={{ maxLength: 500 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>SVG Icon</Typography>
          <Box display="flex" alignItems="center" gap={2} mb={1}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".svg"
              style={{ display: "none" }}
              onChange={handleFileUpload}
            />
            <Button variant="outlined" size="small" onClick={() => fileInputRef.current?.click()}>
              Upload SVG
            </Button>
            {form.svgMarkup && (
              <Box
                sx={{ width: 28, height: 28, color: "#ec7777", "& svg": { width: 28, height: 28 } }}
                dangerouslySetInnerHTML={{ __html: form.svgMarkup }}
              />
            )}
          </Box>
          <TextField
            label="SVG Markup"
            fullWidth
            multiline
            rows={3}
            value={form.svgMarkup}
            onChange={(e) => setForm((f) => ({ ...f, svgMarkup: e.target.value }))}
            sx={{ mb: 2 }}
            size="small"
            placeholder='<svg viewBox="0 0 24 24">...</svg>'
          />
          <Box display="flex" gap={2} alignItems="center">
            <TextField
              label="Sort Order"
              type="number"
              value={form.sortOrder}
              onChange={(e) => setForm((f) => ({ ...f, sortOrder: parseInt(e.target.value) || 0 }))}
              size="small"
              sx={{ width: 120 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.enabled}
                  onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                  size="small"
                />
              }
              label="Enabled"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.label.trim() || !form.url.trim() || !form.svgMarkup.trim()}>
            {editingId !== null ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ────────────────────────── Picasso Labeler ──────────────────────────

interface PicassoQuestion {
  id: number;
  forTsar: string;
  aboutTsar: string;
  deckName: string;
  categoryName: string;
}

const SKIPPED_KEY = "picasso-labeler-skipped";
function loadSkippedIds(): number[] {
  try { return JSON.parse(localStorage.getItem(SKIPPED_KEY) || "[]"); } catch { return []; }
}
function saveSkippedIds(ids: number[]) {
  localStorage.setItem(SKIPPED_KEY, JSON.stringify(ids));
}

function PicassoLabelerPanel({ adminSecret }: { adminSecret: string }) {
  const [question, setQuestion] = useState<PicassoQuestion | null>(null);
  const [totalLabeled, setTotalLabeled] = useState(0);
  const [totalUnlabeled, setTotalUnlabeled] = useState(0);
  const [sessionLabeled, setSessionLabeled] = useState(0);
  const [skippedIds, setSkippedIds] = useState<number[]>(loadSkippedIds);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [exclude, setExclude] = useState("");
  const [language, setLanguage] = useState("");
  const [lastAction, setLastAction] = useState<{ type: "mark" | "skip"; id: number } | null>(null);

  async function fetchNext(currentSkipped?: number[]) {
    const ids = currentSkipped ?? skippedIds;
    setLoading(true);
    try {
      const data = await CommunityApi.getPicassoLabelerNext(adminSecret, {
        exclude: exclude || undefined,
        language: language || undefined,
        skippedIds: ids.length > 0 ? ids : undefined,
      });
      if (!data) return;
      if (data.done) {
        setDone(true);
        setQuestion(null);
        setTotalLabeled(data.totalLabeled ?? 0);
        setTotalUnlabeled(0);
      } else {
        setDone(false);
        setQuestion(data.question);
        setTotalLabeled(data.totalLabeled ?? 0);
        setTotalUnlabeled(data.totalUnlabeled ?? 0);
      }
    } finally {
      setLoading(false);
    }
  }

  async function markPicasso() {
    if (!question || loading) return;
    const markedId = question.id;
    setLoading(true);
    try {
      await CommunityApi.markPicassoSuitable(adminSecret, markedId);
      setSessionLabeled((n) => n + 1);
      setLastAction({ type: "mark", id: markedId });
    } finally {
      fetchNext();
    }
  }

  async function undo() {
    if (!lastAction || loading) return;
    setLoading(true);
    try {
      if (lastAction.type === "mark") {
        await CommunityApi.unmarkPicassoSuitable(adminSecret, lastAction.id);
        setSessionLabeled((n) => Math.max(0, n - 1));
      } else {
        const newSkipped = skippedIds.filter((id) => id !== lastAction.id);
        setSkippedIds(newSkipped);
        saveSkippedIds(newSkipped);
      }
      setLastAction(null);
    } finally {
      setLoading(false);
    }
  }

  function skip() {
    if (!question || loading) return;
    const skippedId = question.id;
    const newSkipped = [...skippedIds, skippedId];
    setSkippedIds(newSkipped);
    saveSkippedIds(newSkipped);
    setLastAction({ type: "skip", id: skippedId });
    fetchNext(newSkipped);
  }

  useEffect(() => {
    fetchNext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === "y" || e.key === "Enter") {
        e.preventDefault();
        markPicasso();
      } else if (e.key === "n" || e.key === " ") {
        e.preventDefault();
        skip();
      } else if (e.key === "z") {
        e.preventDefault();
        undo();
      }
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question, loading, lastAction]);

  if (done) {
    return (
      <Box p={3} textAlign="center">
        <Typography variant="h5" gutterBottom>All Done!</Typography>
        <Typography color="text.secondary">
          All {totalLabeled} questions have been labeled.
        </Typography>
        <Box mt={2} display="flex" justifyContent="center" gap={1}>
          <Chip label={`Labeled this session: ${sessionLabeled}`} color="success" />
          <Chip label={`Skipped: ${skippedIds.length}`} />
        </Box>
      </Box>
    );
  }

  return (
    <Box p={2} maxWidth={700} mx="auto">
      <Box display="flex" flexWrap="wrap" gap={1} mb={2} alignItems="center">
        <Chip label={`Labeled: ${totalLabeled}`} color="success" size="small" />
        <Chip label={`Remaining: ${totalUnlabeled}`} size="small" />
        <Chip label={`Session: +${sessionLabeled}`} variant="outlined" size="small" />
        <Chip label={`Skipped: ${skippedIds.length}`} variant="outlined" size="small"
          onDelete={skippedIds.length > 0 ? () => { setSkippedIds([]); saveSkippedIds([]); fetchNext([]); } : undefined}
        />
      </Box>
      <Box display="flex" gap={1} mb={2}>
        <TextField
          size="small"
          fullWidth
          label="Exclude phrases (comma-separated)"
          placeholder="most to least, rank these"
          value={exclude}
          onChange={(e) => setExclude(e.target.value)}
          onBlur={() => fetchNext()}
          onKeyDown={(e) => { if (e.key === "Enter") { (e.target as HTMLInputElement).blur(); } }}
        />
        <TextField
          size="small"
          select
          label="Lang"
          value={language}
          onChange={(e) => { setLanguage(e.target.value); }}
          SelectProps={{ native: true }}
          sx={{ minWidth: 80 }}
        >
          <option value="">All</option>
          <option value="en">EN</option>
          <option value="de">DE</option>
        </TextField>
      </Box>
      {question ? (
        <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
          <Box display="flex" gap={1} mb={1} flexWrap="wrap">
            <Chip label={question.deckName} size="small" variant="outlined" />
            <Chip label={question.categoryName} size="small" variant="outlined" />
            <Chip label={`#${question.id}`} size="small" variant="outlined" />
          </Box>
          <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>{question.forTsar}</Typography>
          <Typography variant="body2" color="text.secondary">{question.aboutTsar}</Typography>
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ p: 3, mb: 2, textAlign: "center" }}>
          <Typography color="text.secondary">Loading...</Typography>
        </Paper>
      )}
      <Box display="flex" gap={2}>
        <Button
          variant="contained"
          color="success"
          size="large"
          fullWidth
          disabled={loading || !question}
          onClick={markPicasso}
        >
          Picasso (Y / Enter)
        </Button>
        <Button
          variant="outlined"
          size="large"
          fullWidth
          disabled={loading || !question}
          onClick={skip}
        >
          Skip (N / Space)
        </Button>
      </Box>
      {lastAction && (
        <Button
          variant="outlined"
          color="warning"
          fullWidth
          disabled={loading}
          onClick={undo}
          sx={{ mt: 1 }}
        >
          Undo {lastAction.type === "mark" ? "Picasso" : "Skip"} #{lastAction.id} (Z)
        </Button>
      )}
    </Box>
  );
}

// ────────────────────────── Dashboard Panel ──────────────────────────

const GAME_MODE_SHORT: Record<string, string> = {
  STANDARD: "Std",
  FOLLOW_THE_LEADER: "FTL",
  REVERSE_ROUND: "RR",
  PICASSO: "Pic",
  RUN_WITH_THE_HERD: "Herd",
};

const DashboardPanel = React.memo(function DashboardPanel({ lobbies, onSelectLobby }: { lobbies: AdminLobbySnapshot[]; onSelectLobby: (key: string) => void }) {
  const active = lobbies.filter((l) => l.gamePhase !== "F");
  const finished = lobbies.filter((l) => l.gamePhase === "F");
  const totalPlayers = active.reduce((sum, l) => sum + Object.keys(l.players).length, 0);
  const runningCount = active.filter((l) => l.gamePhase === "R").length;
  const inLobbyCount = active.filter((l) => l.gamePhase === "L").length;

  const statCards: { label: string; value: number; color: string; icon: React.ReactNode }[] = [
    { label: "Players Online", value: totalPlayers, color: "#7F97C2", icon: <PeopleIcon sx={{ fontSize: 32, opacity: 0.7 }} /> },
    { label: "Games Running", value: runningCount, color: "#A7C577", icon: <SportsEsportsIcon sx={{ fontSize: 32, opacity: 0.7 }} /> },
    { label: "In Lobby", value: inLobbyCount, color: "#CA8755", icon: <StarIcon sx={{ fontSize: 32, opacity: 0.7 }} /> },
    { label: "Finished", value: finished.length, color: "#D36667", icon: <EmojiEventsIcon sx={{ fontSize: 32, opacity: 0.7 }} /> },
  ];

  return (
    <Box p={3} overflow="auto" flex={1}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Dashboard
      </Typography>

      {/* Stat cards */}
      <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(160px, 1fr))" gap={2} mb={3}>
        {statCards.map((s) => (
          <Paper
            key={s.label}
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${s.color}18 0%, ${s.color}08 100%)`,
              border: `1px solid ${s.color}30`,
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box sx={{ color: s.color }}>{s.icon}</Box>
            <Box>
              <Typography variant="h4" fontWeight="bold" lineHeight={1}>
                {s.value}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {s.label}
              </Typography>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Active lobbies */}
      {active.length > 0 ? (
        <>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Active Lobbies
          </Typography>
          <Box display="flex" flexDirection="column" gap={1.5} mb={3}>
            {active.map((l) => {
              const playerCount = Object.keys(l.players).length;
              const playerNames = Object.values(l.players).map((p) => p.name).join(", ");
              const modes = (l.config.enabledModes ?? []).map((m) => GAME_MODE_SHORT[m] ?? m);
              const isRunning = l.gamePhase === "R";

              return (
                <Paper
                  key={l.lobbyKey}
                  elevation={0}
                  onClick={() => onSelectLobby(l.lobbyKey)}
                  sx={{
                    p: 2,
                    borderRadius: 2.5,
                    border: "1px solid",
                    borderColor: isRunning ? "#A7C57740" : "divider",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      borderColor: isRunning ? "#A7C577" : "#7F97C2",
                      transform: "translateY(-1px)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {l.lobbyKey}
                      </Typography>
                      <Chip
                        label={phaseLabel(l.gamePhase)}
                        color={phaseColor(l.gamePhase)}
                        size="small"
                        sx={{ height: 20, fontSize: 10, fontWeight: 600 }}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {playerCount} player{playerCount !== 1 ? "s" : ""}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                    {isRunning && (
                      <Typography variant="caption" color="text.secondary">
                        Round {l.round}/{l.config.roundLimit}
                      </Typography>
                    )}
                    {modes.length > 0 && (
                      <Box display="flex" gap={0.5}>
                        {modes.map((m) => (
                          <Chip key={m} label={m} size="small" variant="outlined" sx={{ height: 18, fontSize: 9 }} />
                        ))}
                      </Box>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {l.config.roundTimer}s timer
                    </Typography>
                    {l.config.preset && (
                      <Chip label={l.config.preset} size="small" color="secondary" variant="outlined" sx={{ height: 18, fontSize: 9 }} />
                    )}
                    {l.config.deckMode === "topics" && (
                      <Chip label="Topics" size="small" color="info" variant="outlined" sx={{ height: 18, fontSize: 9 }} />
                    )}
                  </Box>
                  {l.config.selectedTopics && l.config.selectedTopics.length > 0 && (
                    <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
                      {l.config.selectedTopics.map((t) => (
                        <Chip key={t} label={t} size="small" variant="outlined" sx={{ height: 18, fontSize: 9 }} />
                      ))}
                    </Box>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                    {playerNames}
                  </Typography>
                </Paper>
              );
            })}
          </Box>
        </>
      ) : (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            textAlign: "center",
            border: "1px dashed",
            borderColor: "divider",
          }}
        >
          <SportsEsportsIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
          <Typography variant="body1" color="text.secondary">
            No active lobbies right now
          </Typography>
        </Paper>
      )}

      {/* Finished lobbies summary */}
      {finished.length > 0 && (
        <>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Recently Finished
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            {finished.slice(0, 8).map((l) => (
              <Chip
                key={l.lobbyKey}
                label={`${l.lobbyKey} (${Object.keys(l.players).length}p)`}
                size="small"
                variant="outlined"
                onClick={() => onSelectLobby(l.lobbyKey)}
                sx={{ cursor: "pointer" }}
              />
            ))}
          </Box>
        </>
      )}
    </Box>
  );
});

// ────────────────────────── Shutdown Panel ──────────────────────────

interface ShutdownMsg { from: string; text: string; ts: number; }

function ShutdownPanel({ socket, minutes, setMinutes, message, setMessage }: {
  socket: Socket | null;
  minutes: number;
  setMinutes: (n: number) => void;
  message: string;
  setMessage: (s: string) => void;
}) {
  const [active, setActive] = useState(false);
  const [messages, setMessages] = useState<ShutdownMsg[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!socket) return;
    socket.emit("getShutdownMessages");

    function onMessages(msgs: ShutdownMsg[]) {
      setMessages(msgs);
      if (msgs.length > 0) setActive(true);
    }
    function onNew(msg: ShutdownMsg) {
      setMessages((prev) => [...prev, msg]);
    }

    socket.on("shutdownMessages", onMessages);
    socket.on("newShutdownMessage", onNew);
    return () => {
      socket.off("shutdownMessages", onMessages);
      socket.off("newShutdownMessage", onNew);
    };
  }, [socket]);

  // Auto-scroll messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  function startShutdown() {
    if (!socket) return;
    socket.emit("startShutdown", { minutes, message });
    setActive(true);
    setMessages([]);
  }

  function cancelShutdown() {
    if (!socket) return;
    socket.emit("cancelShutdown");
    setActive(false);
    setMessages([]);
  }

  return (
    <Box p={2} overflow="auto" flex={1} display="flex" flexDirection="column">
      <Typography variant="h6" gutterBottom>Shutdown Manager</Typography>

      {!active ? (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Minutes until shutdown"
              type="number"
              size="small"
              value={minutes}
              onChange={(e) => setMinutes(Math.max(1, parseInt(e.target.value) || 1))}
              inputProps={{ min: 1 }}
            />
            <TextField
              label="Message to users"
              multiline
              rows={2}
              size="small"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
            <Button variant="contained" color="error" onClick={startShutdown}>
              Start Shutdown ({minutes} min)
            </Button>
          </Box>
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>Shutdown is active</Alert>
          <Button variant="outlined" color="success" onClick={cancelShutdown} fullWidth>
            Cancel Shutdown
          </Button>
        </Paper>
      )}

      <Typography variant="subtitle2" gutterBottom>
        User Messages ({messages.length})
      </Typography>
      <Paper
        variant="outlined"
        sx={{ flex: 1, minHeight: 200, maxHeight: 400, overflow: "auto", p: 1 }}
      >
        {messages.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
            No messages yet
          </Typography>
        ) : (
          messages.map((m, i) => (
            <Box key={i} sx={{ mb: 1, p: 1, borderRadius: 1, bgcolor: "grey.50" }}>
              <Typography variant="caption" color="text.secondary">
                {m.from} &middot; {new Date(m.ts).toLocaleTimeString()}
              </Typography>
              <Typography variant="body2">{m.text}</Typography>
            </Box>
          ))
        )}
        <div ref={messagesEndRef} />
      </Paper>
    </Box>
  );
}

// ────────────────────────── Tagger Panel ──────────────────────────

interface TaggerConfig {
  minPlays: number;
  probeDeckCount: number;
  probeSampleSize: number;
  probeQualityThreshold: number;
  probeMinCategories: number;
  batchSize: number;
  delayMs: number;
  systemPrompt: string;
  isCustomPrompt: boolean;
  cronSchedule: string;
  paused: boolean;
  status: string;
  lastRunAt: string | null;
  lastRunDurationMs: number | null;
  lastRunCardsProcessed: number | null;
  lastRunError: string | null;
  lastRunDecksProbed: number | null;
  lastRunDecksPromoted: number | null;
}

function TaggerPanel({ adminSecret }: { adminSecret: string }) {
  const [config, setConfig] = useState<TaggerConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [runTriggered, setRunTriggered] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(false);
  const [editedPrompt, setEditedPrompt] = useState("");
  const [message, setMessage] = useState<string | null>(null);

  // Editable config fields
  const [minPlays, setMinPlays] = useState(20);
  const [probeDeckCount, setProbeDeckCount] = useState(3);
  const [probeSampleSize, setProbeSampleSize] = useState(5);
  const [probeQualityThreshold, setProbeQualityThreshold] = useState(0.6);
  const [probeMinCategories, setProbeMinCategories] = useState(3);

  const headers: Record<string, string> = { "x-admin-secret": adminSecret, "Content-Type": "application/json" };
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  async function loadConfig() {
    setLoading(true);
    try {
      const resp = await apiFetch("/api/admin/tagger/config", { headers });
      if (!resp.ok) throw new Error("Failed to fetch");
      const data: TaggerConfig = await resp.json();
      setConfig(data);
      setMinPlays(data.minPlays);
      setProbeDeckCount(data.probeDeckCount);
      setProbeSampleSize(data.probeSampleSize);
      setProbeQualityThreshold(data.probeQualityThreshold);
      setProbeMinCategories(data.probeMinCategories);
      if (data.status !== "running" && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setRunTriggered(false);
      }
    } catch {
      setConfig(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadConfig();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function saveConfig() {
    setSaving(true);
    setMessage(null);
    try {
      const body: Record<string, any> = {};
      if (minPlays !== config?.minPlays) body.minPlays = minPlays;
      if (probeDeckCount !== config?.probeDeckCount) body.probeDeckCount = probeDeckCount;
      if (probeSampleSize !== config?.probeSampleSize) body.probeSampleSize = probeSampleSize;
      if (probeQualityThreshold !== config?.probeQualityThreshold) body.probeQualityThreshold = probeQualityThreshold;
      if (probeMinCategories !== config?.probeMinCategories) body.probeMinCategories = probeMinCategories;
      if (Object.keys(body).length === 0) { setSaving(false); return; }
      await apiFetch("/api/admin/tagger/config", { method: "PATCH", headers, body: JSON.stringify(body) });
      setMessage("Saved");
      loadConfig();
    } catch (e: any) {
      setMessage("Error: " + e.message);
    } finally {
      setSaving(false);
    }
  }

  async function togglePaused() {
    try {
      await apiFetch("/api/admin/tagger/config", {
        method: "PATCH", headers,
        body: JSON.stringify({ paused: !config?.paused }),
      });
      loadConfig();
    } catch {}
  }

  async function triggerRun() {
    setRunTriggered(true);
    setMessage(null);
    try {
      const resp = await apiFetch("/api/admin/tagger/run", { method: "POST", headers });
      if (resp.status === 409) { setMessage("Already running"); setRunTriggered(false); return; }
      if (!resp.ok) throw new Error("Failed");
      setMessage("Run started");
      // Poll for completion
      pollRef.current = setInterval(loadConfig, 3000);
    } catch (e: any) {
      setMessage("Error: " + e.message);
      setRunTriggered(false);
    }
  }

  async function savePrompt() {
    setSaving(true);
    try {
      await apiFetch("/api/admin/tagger/config", {
        method: "PATCH", headers,
        body: JSON.stringify({ systemPromptOverride: editedPrompt }),
      });
      setEditingPrompt(false);
      setMessage("Prompt saved");
      loadConfig();
    } catch (e: any) {
      setMessage("Error: " + e.message);
    } finally {
      setSaving(false);
    }
  }

  async function resetPrompt() {
    if (!window.confirm("Reset prompt to factory default?")) return;
    try {
      const resp = await apiFetch("/api/admin/tagger/reset-prompt", { method: "POST", headers });
      const data = await resp.json();
      setEditedPrompt(data.prompt);
      setEditingPrompt(false);
      setMessage("Prompt reset to default");
      loadConfig();
    } catch (e: any) {
      setMessage("Error: " + e.message);
    }
  }

  async function resetAllToDefaults() {
    if (!window.confirm("Reset ALL tagger settings to factory defaults?")) return;
    try {
      await apiFetch("/api/admin/tagger/config", {
        method: "PATCH", headers,
        body: JSON.stringify({
          minPlays: null, probeDeckCount: null, probeSampleSize: null,
          probeQualityThreshold: null, probeMinCategories: null,
          batchSize: null, delayMs: null, systemPromptOverride: null,
        }),
      });
      setMessage("All settings reset to defaults");
      loadConfig();
    } catch (e: any) {
      setMessage("Error: " + e.message);
    }
  }

  if (loading && !config) return <Box p={3}><Typography>Loading...</Typography></Box>;

  const statusColor = config?.status === "running" ? "warning" : config?.status === "failed" ? "error" : "success";

  return (
    <Box p={2} display="flex" flexDirection="column" gap={2}>
      {message && <Alert severity="info" onClose={() => setMessage(null)}>{message}</Alert>}

      {/* Run Status */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Run Status</Typography>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" mb={1}>
          <Chip label={config?.status ?? "unknown"} color={statusColor} size="small" />
          <Chip label={config?.paused ? "PAUSED" : "Active"} color={config?.paused ? "warning" : "default"} size="small" variant="outlined" />
          <Typography variant="body2" color="text.secondary">
            Cron: {config?.cronSchedule ?? "—"}
          </Typography>
        </Box>
        {config?.lastRunAt && (
          <Box mb={1}>
            <Typography variant="body2">
              Last run: {new Date(config.lastRunAt).toLocaleString()}
              {config.lastRunDurationMs != null && ` (${(config.lastRunDurationMs / 1000).toFixed(1)}s)`}
            </Typography>
            <Typography variant="body2">
              Cards: {config.lastRunCardsProcessed ?? 0}
              {" | "}Probed: {config.lastRunDecksProbed ?? 0}
              {" | "}Promoted: {config.lastRunDecksPromoted ?? 0}
            </Typography>
          </Box>
        )}
        {config?.lastRunError && (
          <Alert severity="error" sx={{ mb: 1 }}>{config.lastRunError}</Alert>
        )}
        <Box display="flex" gap={1}>
          <Button size="small" variant="outlined" onClick={loadConfig} disabled={loading}>
            Refresh
          </Button>
          <Button size="small" variant="contained" onClick={triggerRun}
            disabled={runTriggered || config?.status === "running"}>
            Run Now
          </Button>
          <Button size="small" variant={config?.paused ? "contained" : "outlined"}
            color={config?.paused ? "warning" : "primary"} onClick={togglePaused}>
            {config?.paused ? "Unpause" : "Pause"}
          </Button>
        </Box>
      </Paper>

      {/* Configuration */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Configuration</Typography>
        <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
          <TextField label="Min Plays" type="number" size="small" sx={{ width: 120 }}
            value={minPlays} onChange={(e) => setMinPlays(Number(e.target.value))} />
          <TextField label="Probe Decks" type="number" size="small" sx={{ width: 120 }}
            value={probeDeckCount} onChange={(e) => setProbeDeckCount(Number(e.target.value))} />
          <TextField label="Probe Samples" type="number" size="small" sx={{ width: 120 }}
            value={probeSampleSize} onChange={(e) => setProbeSampleSize(Number(e.target.value))} />
          <TextField label="Quality Threshold" type="number" size="small" sx={{ width: 140 }}
            inputProps={{ step: 0.1, min: 0, max: 1 }}
            value={probeQualityThreshold} onChange={(e) => setProbeQualityThreshold(Number(e.target.value))} />
          <TextField label="Min Categories" type="number" size="small" sx={{ width: 140 }}
            value={probeMinCategories} onChange={(e) => setProbeMinCategories(Number(e.target.value))} />
        </Box>
        <Box display="flex" gap={1}>
          <Button size="small" variant="contained" onClick={saveConfig} disabled={saving}>Save</Button>
          <Button size="small" variant="outlined" color="warning" onClick={resetAllToDefaults}>Reset All to Defaults</Button>
        </Box>
      </Paper>

      {/* System Prompt */}
      <Paper sx={{ p: 2 }}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <Typography variant="h6">System Prompt</Typography>
          <Chip label={config?.isCustomPrompt ? "Custom" : "Default"} size="small"
            color={config?.isCustomPrompt ? "primary" : "default"} variant="outlined" />
        </Box>
        {editingPrompt ? (
          <>
            <TextField multiline rows={20} fullWidth variant="outlined" size="small"
              value={editedPrompt} onChange={(e) => setEditedPrompt(e.target.value)}
              sx={{ fontFamily: "monospace", fontSize: 12, mb: 1 }} />
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
              {editedPrompt.length} characters
            </Typography>
            <Box display="flex" gap={1}>
              <Button size="small" variant="contained" onClick={savePrompt} disabled={saving}>Save Prompt</Button>
              <Button size="small" variant="outlined" onClick={() => setEditingPrompt(false)}>Cancel</Button>
              <Button size="small" variant="outlined" color="warning" onClick={resetPrompt}>Reset to Default</Button>
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ maxHeight: 300, overflow: "auto", bgcolor: "action.hover", borderRadius: 1, p: 1.5, mb: 1 }}>
              <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 12 }}>
                {config?.systemPrompt ?? ""}
              </pre>
            </Box>
            <Button size="small" variant="outlined" onClick={() => { setEditedPrompt(config?.systemPrompt ?? ""); setEditingPrompt(true); }}>
              Edit Prompt
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
}

// ────────────────────────── Personality Tagger Panel ──────────────────────────

interface PersonalityConfig {
  batchSize: number;
  delayMs: number;
  systemPrompt: string;
  isCustomPrompt: boolean;
  paused: boolean;
  status: string;
  lastRunAt: string | null;
  lastRunDurationMs: number | null;
  lastRunCardsProcessed: number | null;
  lastRunError: string | null;
  remainingUnscored: number;
  deckBacklog: number;
}

function PersonalityTaggerPanel({ adminSecret }: { adminSecret: string }) {
  const [config, setConfig] = useState<PersonalityConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [runTriggered, setRunTriggered] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(false);
  const [editedPrompt, setEditedPrompt] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [batchSize, setBatchSize] = useState(50);
  const [delayMs, setDelayMs] = useState(120);
  const [runLimit, setRunLimit] = useState("");
  const [deckRunCount, setDeckRunCount] = useState("");

  const headers: Record<string, string> = { "x-admin-secret": adminSecret, "Content-Type": "application/json" };
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  async function loadConfig() {
    setLoading(true);
    try {
      const resp = await apiFetch("/api/admin/personality-tagger/config", { headers });
      if (!resp.ok) throw new Error("Failed to fetch");
      const data: PersonalityConfig = await resp.json();
      setConfig(data);
      setBatchSize(data.batchSize);
      setDelayMs(data.delayMs);
      if (data.status !== "running" && pollRef.current) {
        clearInterval(pollRef.current); pollRef.current = null; setRunTriggered(false);
      }
    } catch { setConfig(null); } finally { setLoading(false); }
  }

  useEffect(() => {
    loadConfig();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function saveConfig() {
    setSaving(true); setMessage(null);
    try {
      const body: Record<string, any> = {};
      if (batchSize !== config?.batchSize) body.batchSize = batchSize;
      if (delayMs !== config?.delayMs) body.delayMs = delayMs;
      if (Object.keys(body).length === 0) { setSaving(false); return; }
      await apiFetch("/api/admin/personality-tagger/config", { method: "PATCH", headers, body: JSON.stringify(body) });
      setMessage("Saved"); loadConfig();
    } catch (e: any) { setMessage("Error: " + e.message); } finally { setSaving(false); }
  }

  async function togglePaused() {
    try {
      await apiFetch("/api/admin/personality-tagger/config", { method: "PATCH", headers, body: JSON.stringify({ paused: !config?.paused }) });
      loadConfig();
    } catch {}
  }

  async function triggerRun() {
    setRunTriggered(true); setMessage(null);
    try {
      const limit = runLimit.trim() ? Number(runLimit) : undefined;
      const resp = await apiFetch("/api/admin/personality-tagger/run", { method: "POST", headers, body: JSON.stringify({ limit }) });
      if (resp.status === 409) { setMessage("Already running"); setRunTriggered(false); return; }
      if (!resp.ok) throw new Error("Failed");
      setMessage(limit ? `Run started (limit ${limit})` : "Run started (all unscored)");
      pollRef.current = setInterval(loadConfig, 3000);
    } catch (e: any) { setMessage("Error: " + e.message); setRunTriggered(false); }
  }

  async function triggerDeckRun() {
    const n = deckRunCount.trim() ? Number(deckRunCount) : 0;
    if (!n) { setMessage("Enter how many decks to tag"); return; }
    setRunTriggered(true); setMessage(null);
    try {
      const resp = await apiFetch("/api/admin/personality-tagger/run-decks", { method: "POST", headers, body: JSON.stringify({ deckCount: n }) });
      if (resp.status === 409) { setMessage("Already running"); setRunTriggered(false); return; }
      if (!resp.ok) throw new Error("Failed");
      setMessage(`Tagging cards in ${n} deck(s)`);
      pollRef.current = setInterval(loadConfig, 3000);
    } catch (e: any) { setMessage("Error: " + e.message); setRunTriggered(false); }
  }

  async function savePrompt() {
    setSaving(true);
    try {
      await apiFetch("/api/admin/personality-tagger/config", { method: "PATCH", headers, body: JSON.stringify({ systemPromptOverride: editedPrompt }) });
      setEditingPrompt(false); setMessage("Prompt saved"); loadConfig();
    } catch (e: any) { setMessage("Error: " + e.message); } finally { setSaving(false); }
  }

  async function resetPrompt() {
    if (!window.confirm("Reset prompt to factory default?")) return;
    try {
      const resp = await apiFetch("/api/admin/personality-tagger/reset-prompt", { method: "POST", headers });
      const data = await resp.json();
      setEditedPrompt(data.prompt); setEditingPrompt(false); setMessage("Prompt reset to default"); loadConfig();
    } catch (e: any) { setMessage("Error: " + e.message); }
  }

  if (loading && !config) return <Box p={3}><Typography>Loading...</Typography></Box>;
  const statusColor = config?.status === "running" ? "warning" : config?.status === "failed" ? "error" : "success";

  return (
    <Box p={2} display="flex" flexDirection="column" gap={2}>
      {message && <Alert severity="info" onClose={() => setMessage(null)}>{message}</Alert>}
      <Alert severity="info" sx={{ mb: 0 }}>
        Scores each question 1–10 for how <b>individual-vs-group</b> it is. Versus mode excludes questions scoring ≥ 7.
        Only scores cards where <code>personality</code> is still empty.
      </Alert>

      {/* Run Status */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Run Status</Typography>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" mb={1}>
          <Chip label={config?.status ?? "unknown"} color={statusColor} size="small" />
          <Chip label={config?.paused ? "PAUSED" : "Active"} color={config?.paused ? "warning" : "default"} size="small" variant="outlined" />
          <Chip label={`${config?.remainingUnscored ?? 0} unscored`} size="small" variant="outlined" />
        </Box>
        {config?.lastRunAt && (
          <Typography variant="body2" mb={1}>
            Last run: {new Date(config.lastRunAt).toLocaleString()}
            {config.lastRunDurationMs != null && ` (${(config.lastRunDurationMs / 1000).toFixed(1)}s)`}
            {" | "}Scored: {config.lastRunCardsProcessed ?? 0}
          </Typography>
        )}
        {config?.lastRunError && <Alert severity="error" sx={{ mb: 1 }}>{config.lastRunError}</Alert>}
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          <Button size="small" variant="outlined" onClick={loadConfig} disabled={loading}>Refresh</Button>
          <TextField label="Limit (blank = all)" size="small" sx={{ width: 150 }} value={runLimit}
            onChange={(e) => setRunLimit(e.target.value.replace(/[^0-9]/g, ""))} />
          <Button size="small" variant="contained" onClick={triggerRun} disabled={runTriggered || config?.status === "running"}>Run Now</Button>
          <Button size="small" variant={config?.paused ? "contained" : "outlined"} color={config?.paused ? "warning" : "primary"} onClick={togglePaused}>
            {config?.paused ? "Unpause" : "Pause"}
          </Button>
        </Box>
      </Paper>

      {/* Finish topic-tagged decks (tagged by tagger 1, not yet by tagger 2) */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Finish topic-tagged decks</Typography>
        <Typography variant="body2" color="text.secondary" mb={1.5}>
          <b>{config?.deckBacklog ?? 0}</b> deck(s) have cards tagged by the topic tagger but not yet scored for personality.
          Enter how many of those decks to score now — it only touches the topic-tagged, still-unscored cards in them.
        </Typography>
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          <TextField label="Decks to tag" size="small" sx={{ width: 130 }} value={deckRunCount}
            onChange={(e) => setDeckRunCount(e.target.value.replace(/[^0-9]/g, ""))} />
          <Button size="small" variant="contained" onClick={triggerDeckRun}
            disabled={runTriggered || config?.status === "running" || !(config?.deckBacklog)}>
            Tag these decks
          </Button>
        </Box>
      </Paper>

      {/* Configuration */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Configuration</Typography>
        <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
          <TextField label="Batch Size" type="number" size="small" sx={{ width: 120 }} value={batchSize} onChange={(e) => setBatchSize(Number(e.target.value))} />
          <TextField label="Delay (ms)" type="number" size="small" sx={{ width: 120 }} value={delayMs} onChange={(e) => setDelayMs(Number(e.target.value))} />
        </Box>
        <Button size="small" variant="contained" onClick={saveConfig} disabled={saving}>Save</Button>
      </Paper>

      {/* System Prompt */}
      <Paper sx={{ p: 2 }}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <Typography variant="h6">System Prompt</Typography>
          <Chip label={config?.isCustomPrompt ? "Custom" : "Default"} size="small" color={config?.isCustomPrompt ? "primary" : "default"} variant="outlined" />
        </Box>
        {editingPrompt ? (
          <>
            <TextField multiline rows={22} fullWidth variant="outlined" size="small" value={editedPrompt} onChange={(e) => setEditedPrompt(e.target.value)} sx={{ fontFamily: "monospace", fontSize: 12, mb: 1 }} />
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>{editedPrompt.length} characters. Must ask for JSON: {"{"}"personality": &lt;1-10&gt;{"}"}.</Typography>
            <Box display="flex" gap={1}>
              <Button size="small" variant="contained" onClick={savePrompt} disabled={saving}>Save Prompt</Button>
              <Button size="small" variant="outlined" onClick={() => setEditingPrompt(false)}>Cancel</Button>
              <Button size="small" variant="outlined" color="warning" onClick={resetPrompt}>Reset to Default</Button>
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ maxHeight: 300, overflow: "auto", bgcolor: "action.hover", borderRadius: 1, p: 1.5, mb: 1 }}>
              <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 12 }}>{config?.systemPrompt ?? ""}</pre>
            </Box>
            <Button size="small" variant="outlined" onClick={() => { setEditedPrompt(config?.systemPrompt ?? ""); setEditingPrompt(true); }}>Edit Prompt</Button>
          </>
        )}
      </Paper>
    </Box>
  );
}

// ────────────────────────── Sidebar ──────────────────────────

type ActiveView = "dashboard" | "lobby" | "feedback" | "reports" | "content" | "history" | "analytics" | "tagger" | "personality-tagger" | "announcements" | "intermissions" | "cta-cards" | "social-links" | "picasso" | "shutdown";

interface AdminReply { from: string; lobbyKey: string; playerId: string; text: string; ts: number }

// Tool views, grouped for the nav bar above the content area. Groups wrap as a
// unit so related buttons stay together on narrow screens.
const VIEW_GROUPS: { label: string; views: { id: ActiveView; label: string }[] }[] = [
  {
    label: "Live",
    views: [{ id: "dashboard", label: "Dashboard" }],
  },
  {
    label: "Content",
    views: [
      { id: "content", label: "Content Stats" },
      { id: "tagger", label: "Topic Tagger" },
      { id: "personality-tagger", label: "Personality Tagger" },
      { id: "picasso", label: "Picasso Labeler" },
      { id: "reports", label: "Deck Reports" },
    ],
  },
  {
    label: "Community",
    views: [
      { id: "feedback", label: "Feedback" },
      { id: "announcements", label: "Announcements" },
      { id: "intermissions", label: "Intermissions" },
      { id: "cta-cards", label: "CTA Cards" },
      { id: "social-links", label: "Social Links" },
    ],
  },
  {
    label: "Data",
    views: [
      { id: "history", label: "Match History" },
      { id: "analytics", label: "Analytics" },
    ],
  },
  {
    label: "System",
    views: [{ id: "shutdown", label: "Shutdown" }],
  },
];

const VIEW_LABELS: Record<ActiveView, string> = VIEW_GROUPS.reduce(
  (acc, group) => {
    group.views.forEach((v) => {
      acc[v.id] = v.label;
    });
    return acc;
  },
  { lobby: "Lobby" } as Record<ActiveView, string>
);

function replyTime(ts: number) {
  return new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

// ────────────────────────── View nav bar ──────────────────────────

// `stacked` renders the groups as a vertical list for the mobile drawer. The
// horizontal bar variant wraps into many rows on a phone and would eat half the
// viewport, so on mobile the nav lives in the drawer instead of above the content.
const ViewNav = React.memo(function ViewNav({
  activeView,
  onSelect,
  stacked = false,
}: {
  activeView: ActiveView;
  onSelect: (v: ActiveView) => void;
  stacked?: boolean;
}) {
  return (
    <Box
      display="flex"
      flexDirection={stacked ? "column" : "row"}
      flexWrap={stacked ? "nowrap" : "wrap"}
      alignItems={stacked ? "stretch" : "center"}
      gap={stacked ? 0.75 : 1.5}
      px={1}
      py={0.75}
      flexShrink={0}
      sx={{
        borderBottom: stacked ? undefined : "1px solid rgba(0,0,0,0.08)",
        background: stacked ? undefined : "rgba(255,255,255,0.8)",
      }}
    >
      {VIEW_GROUPS.map((group) => (
        <Box
          key={group.label}
          display="flex"
          flexDirection={stacked ? "column" : "row"}
          flexWrap="wrap"
          alignItems={stacked ? "stretch" : "center"}
          gap={0.25}
          sx={{
            px: 0.75,
            py: 0.25,
            maxWidth: "100%",
            borderRadius: 1.5,
            background: "rgba(0,0,0,0.035)",
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textTransform: "uppercase", fontSize: 9, fontWeight: "bold", letterSpacing: 0.5, mr: 0.25 }}
          >
            {group.label}
          </Typography>
          {group.views.map((v) => (
            <Button
              key={v.id}
              size="small"
              variant={activeView === v.id ? "contained" : "text"}
              color={v.id === "shutdown" ? "error" : "primary"}
              onClick={() => onSelect(v.id)}
              sx={{
                minWidth: 0,
                px: 1,
                py: 0.25,
                fontSize: 12,
                whiteSpace: "nowrap",
                ...(stacked ? { justifyContent: "flex-start" } : null),
              }}
            >
              {v.label}
            </Button>
          ))}
        </Box>
      ))}
    </Box>
  );
});

const SidebarContent = React.memo(function SidebarContent({
  lobbyList,
  selectedKey,
  setSelectedKey,
  activeView,
  setActiveView,
  broadcastMsg,
  setBroadcastMsg,
  sendBroadcast,
  finishedOpen,
  setFinishedOpen,
  unreadReplies,
  openReplies,
  logout,
  onClose,
  viewNav,
}: {
  lobbyList: AdminLobbySnapshot[];
  selectedKey: string | null;
  setSelectedKey: (k: string | null) => void;
  activeView: ActiveView;
  setActiveView: (v: ActiveView) => void;
  broadcastMsg: string;
  setBroadcastMsg: (v: string) => void;
  sendBroadcast: () => void;
  finishedOpen: boolean;
  setFinishedOpen: (v: boolean) => void;
  unreadReplies: number;
  openReplies: () => void;
  logout: () => void;
  onClose?: () => void;
  /** Rendered above the lobby list — used by the mobile drawer to host the view nav. */
  viewNav?: React.ReactNode;
}) {
  const activeLobbies = lobbyList.filter((l) => l.gamePhase !== "F");
  const finishedLobbies = lobbyList.filter((l) => l.gamePhase === "F");

  function selectLobby(key: string) {
    setSelectedKey(key);
    setActiveView("lobby");
    onClose?.();
  }

  return (
    <Box display="flex" flexDirection="column" height="100%" overflow="hidden">
      <Box px={1} pt={1} pb={0.5} display="flex" alignItems="center" gap={0.5}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ flex: 1 }}>
          rank.io admin
        </Typography>
        <Tooltip title="Player replies">
          <IconButton size="small" onClick={openReplies}>
            <Badge badgeContent={unreadReplies} color="error" max={99}>
              <ChatBubbleOutlineIcon fontSize="small" />
            </Badge>
          </IconButton>
        </Tooltip>
        {onClose && (
          <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
        )}
      </Box>
      <Divider />
      <Box flex={1} minHeight={0} display="flex" flexDirection="column" sx={{ overflowY: "auto" }}>
      {viewNav && (
        <>
          {viewNav}
          <Divider />
        </>
      )}
      <Box px={1} py={0.5} display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ textTransform: "uppercase" }}>
          Lobbies
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {activeLobbies.length} active
        </Typography>
      </Box>
      <List dense disablePadding>
        {lobbyList.length === 0 && (
          <Box p={2}>
            <Typography variant="body2" color="text.secondary">
              No active lobbies
            </Typography>
          </Box>
        )}
        {activeLobbies.map((lobby) => (
          <ListItemButton
            key={lobby.lobbyKey}
            selected={activeView === "lobby" && selectedKey === lobby.lobbyKey}
            onClick={() => selectLobby(lobby.lobbyKey)}
          >
            <ListItemText
              primary={lobby.lobbyKey}
              secondary={
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Chip
                    label={phaseLabel(lobby.gamePhase)}
                    size="small"
                    color={phaseColor(lobby.gamePhase)}
                    sx={{ height: 16, fontSize: 10 }}
                  />
                  <span>{Object.keys(lobby.players).length} players</span>
                </Box>
              }
            />
          </ListItemButton>
        ))}

        {/* Finished lobbies — collapsed by default */}
        {finishedLobbies.length > 0 && (
          <>
            <Divider />
            <ListItemButton onClick={() => setFinishedOpen(!finishedOpen)} sx={{ py: 0.5 }}>
              <ListItemText
                primary={
                  <Typography variant="caption" color="text.secondary">
                    {finishedOpen ? "▾" : "▸"} Finished ({finishedLobbies.length})
                  </Typography>
                }
              />
            </ListItemButton>
            <Collapse in={finishedOpen}>
              {finishedLobbies.map((lobby) => (
                <ListItemButton
                  key={lobby.lobbyKey}
                  selected={activeView === "lobby" && selectedKey === lobby.lobbyKey}
                  onClick={() => selectLobby(lobby.lobbyKey)}
                  sx={{ opacity: 0.7 }}
                >
                  <ListItemText
                    primary={lobby.lobbyKey}
                    secondary={
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Chip label="FINISHED" size="small" color="error" sx={{ height: 16, fontSize: 10 }} />
                        <span>{Object.keys(lobby.players).length} players</span>
                      </Box>
                    }
                  />
                </ListItemButton>
              ))}
            </Collapse>
          </>
        )}
      </List>
      </Box>

      {/* Broadcast to all lobbies + logout */}
      <Box
        sx={{
          borderTop: "1px solid #ccc",
          p: 1,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          flexShrink: 0,
        }}
      >
        <Box display="flex" gap={0.5}>
          <TextField
            size="small"
            fullWidth
            value={broadcastMsg}
            onChange={(e) => setBroadcastMsg(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendBroadcast()}
            placeholder="Broadcast to all…"
            inputProps={{ style: { fontSize: 12 } }}
          />
          <Button
            size="small"
            variant="contained"
            color="warning"
            onClick={sendBroadcast}
            disabled={!broadcastMsg.trim()}
            sx={{ flexShrink: 0, minWidth: 0, px: 1 }}
          >
            Send
          </Button>
        </Box>
        <Button size="small" fullWidth variant="text" color="error" onClick={logout} sx={{ py: 0 }}>
          Logout
        </Button>
      </Box>
    </Box>
  );
});

// ────────────────────────── Replies dialog ──────────────────────────

function RepliesDialog({
  open,
  onClose,
  replies,
  onSelectLobby,
  onClear,
  lastSeenTs,
}: {
  open: boolean;
  onClose: () => void;
  replies: AdminReply[];
  onSelectLobby: (key: string) => void;
  onClear: () => void;
  lastSeenTs: number;
}) {
  const ordered = [...replies].sort((a, b) => b.ts - a.ts);
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1 }}>
        <Box flex={1}>Player replies ({replies.length})</Box>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ maxHeight: "60vh" }}>
        {ordered.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No replies yet. Messages players send back arrive here — they are kept on the server, so
            they survive a logout (until the server restarts).
          </Typography>
        ) : (
          ordered.map((r, i) => (
            <Box
              key={`${r.ts}-${r.playerId}-${i}`}
              sx={{
                mb: 0.5,
                p: 1,
                borderRadius: 1,
                bgcolor: r.ts > lastSeenTs ? "rgba(211,102,103,0.12)" : "action.hover",
              }}
            >
              <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                <Typography variant="caption" fontWeight="bold">{r.from}</Typography>
                {r.lobbyKey && (
                  <Chip
                    label={r.lobbyKey}
                    size="small"
                    sx={{ height: 18, fontSize: 10, cursor: "pointer" }}
                    onClick={() => { onSelectLobby(r.lobbyKey); onClose(); }}
                  />
                )}
                <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
                  {replyTime(r.ts)}
                </Typography>
              </Box>
              <Typography variant="body2">{r.text}</Typography>
            </Box>
          ))
        )}
      </DialogContent>
      <DialogActions>
        <Button size="small" color="error" onClick={onClear} disabled={replies.length === 0}>
          Clear all
        </Button>
        <Button size="small" onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ────────────────────────── Main component ──────────────────────────

const SIDEBAR_WIDTH = 220;

export default function AdminPanel() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [password, setPassword] = useState(() => sessionStorage.getItem("admin-secret") || "");
  const [error, setError] = useState<string | null>(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [lobbies, setLobbies] = useState<Record<string, AdminLobbySnapshot>>({});
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<ActiveView>("dashboard");
  const [finishedOpen, setFinishedOpen] = useState(false);
  const [broadcastMsg, setBroadcastMsg] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [messageReplies, setMessageReplies] = useState<AdminReply[]>([]);
  const [repliesOpen, setRepliesOpen] = useState(false);
  const [lastSeenReplyTs, setLastSeenReplyTs] = useState<number>(
    () => Number(localStorage.getItem("admin-replies-seen") || 0)
  );
  const [shutdownMinutes, setShutdownMinutes] = useState(10);
  const [shutdownMessage, setShutdownMessage] = useState("Scheduled maintenance — the server will restart shortly.");
  const socketRef = useRef<Socket | null>(null);

  const sendBroadcast = useCallback(() => {
    if (!broadcastMsg.trim() || !socketRef.current) return;
    socketRef.current.emit("broadcastMessage", { message: broadcastMsg.trim() });
    setBroadcastMsg("");
  }, [broadcastMsg]);

  function connectWith(secret: string) {
    if (socketRef.current) {
      socketRef.current.disconnect();
    }
    const s = io(socketNamespace("/_rankadmin"), {
      autoConnect: false,
      auth: { secret },
      withCredentials: true,
    });
    socketRef.current = s;

    s.on("connect_error", (err) => {
      setError("Wrong password or connection failed: " + err.message);
      sessionStorage.removeItem("admin-secret");
      s.disconnect();
    });

    s.on("update", (data: Record<string, AdminLobbySnapshot>) => {
      setLobbies(data);
      setAuthenticated(true);
      setError(null);
      sessionStorage.setItem("admin-secret", secret);
    });

    // Server keeps a buffer of replies so they survive an admin logout/login
    s.on("adminMessageReplyHistory", (history: AdminReply[]) => {
      setMessageReplies(Array.isArray(history) ? history : []);
    });

    s.on("adminMessageReply", (reply: AdminReply) => {
      setMessageReplies((prev) => [...prev, reply]);
    });

    s.connect();
  }

  function connect() {
    connectWith(password);
  }

  function logout() {
    sessionStorage.removeItem("admin-secret");
    socketRef.current?.disconnect();
    socketRef.current = null;
    setAuthenticated(false);
    setPassword("");
    setLobbies({});
    setSelectedKey(null);
  }

  // Auto-connect if we have a saved secret
  useEffect(() => {
    const saved = sessionStorage.getItem("admin-secret");
    if (saved) {
      connectWith(saved);
    }
    return () => {
      socketRef.current?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deselect lobby if it disappears
  useEffect(() => {
    if (selectedKey && !(selectedKey in lobbies)) {
      setSelectedKey(null);
    }
  }, [lobbies, selectedKey]);

  const lobbyList = useMemo(() => Object.values(lobbies), [lobbies]);
  const selectedLobby = selectedKey ? lobbies[selectedKey] : null;
  const handleSelectLobby = useCallback((key: string) => { setSelectedKey(key); setActiveView("lobby"); }, []);

  if (!authenticated) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100vh"
        gap={2}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            background: "rgba(255,255,255,0.85)",
            border: "1px solid rgba(255,255,255,0.5)",
            minWidth: 300,
          }}
        >
          <Typography variant="h5" fontWeight="bold">Admin Panel</Typography>
          {error && <Alert severity="error" sx={{ width: "100%" }}>{error}</Alert>}
          <TextField
            label="Admin Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && connect()}
            size="small"
            fullWidth
          />
          <Button variant="contained" onClick={connect} fullWidth>
            Connect
          </Button>
        </Paper>
      </Box>
    );
  }

  const unreadReplies = messageReplies.filter((r) => r.ts > lastSeenReplyTs).length;

  function openReplies() {
    setRepliesOpen(true);
  }

  function closeReplies() {
    setRepliesOpen(false);
    const newest = messageReplies.reduce((max, r) => Math.max(max, r.ts), lastSeenReplyTs);
    setLastSeenReplyTs(newest);
    localStorage.setItem("admin-replies-seen", String(newest));
  }

  function clearReplies() {
    socketRef.current?.emit("clearMessageReplies");
    setMessageReplies([]);
  }

  function switchView(v: ActiveView) {
    setActiveView(v);
    setSelectedKey(null);
    setDrawerOpen(false);
  }

  const sidebarProps = {
    lobbyList,
    selectedKey,
    setSelectedKey,
    activeView,
    setActiveView,
    broadcastMsg,
    setBroadcastMsg,
    sendBroadcast,
    finishedOpen,
    setFinishedOpen,
    unreadReplies,
    openReplies,
    logout,
  };

  const mainContent = (
    <Box flex={1} overflow="auto" display="flex" flexDirection="column" sx={{ background: "rgba(255,255,255,0.65)" }}>
      {/* Mobile top bar */}
      {isMobile && (
        <Box
          display="flex"
          alignItems="center"
          px={1}
          py={0.5}
          borderBottom="1px solid rgba(0,0,0,0.08)"
          flexShrink={0}
          sx={{ background: "rgba(255,255,255,0.8)" }}
        >
          <IconButton onClick={() => setDrawerOpen(true)} size="small">
            <MenuIcon />
          </IconButton>
          <Typography variant="subtitle2" sx={{ ml: 1, flex: 1 }}>
            {activeView === "lobby"
              ? selectedLobby
                ? selectedLobby.lobbyKey
                : "Select a lobby"
              : VIEW_LABELS[activeView]}
          </Typography>
          <IconButton size="small" onClick={openReplies}>
            <Badge badgeContent={unreadReplies} color="error" max={99}>
              <ChatBubbleOutlineIcon fontSize="small" />
            </Badge>
          </IconButton>
        </Box>
      )}
      {/* On mobile the nav lives in the drawer — see SidebarContent viewNav */}
      {!isMobile && <ViewNav activeView={activeView} onSelect={switchView} />}
      <Box flex={1} overflow="auto">
        {activeView === "dashboard" ? (
          <DashboardPanel lobbies={lobbyList} onSelectLobby={handleSelectLobby} />
        ) : activeView === "feedback" ? (
          <FeedbackPanel adminSecret={password} />
        ) : activeView === "reports" ? (
          <DeckReportsPanel adminSecret={password} />
        ) : activeView === "content" ? (
          <ContentStatsPanel adminSecret={password} />
        ) : activeView === "history" ? (
          <MatchHistoryPanel adminSecret={password} />
        ) : activeView === "analytics" ? (
          <AnalyticsPanel adminSecret={password} />
        ) : activeView === "tagger" ? (
          <TaggerPanel adminSecret={password} />
        ) : activeView === "personality-tagger" ? (
          <PersonalityTaggerPanel adminSecret={password} />
        ) : activeView === "announcements" ? (
          <AnnouncementsPanel adminSecret={password} />
        ) : activeView === "intermissions" ? (
          <IntermissionsPanel adminSecret={password} />
        ) : activeView === "cta-cards" ? (
          <CtaCardsPanel adminSecret={password} />
        ) : activeView === "social-links" ? (
          <SocialLinksPanel adminSecret={password} />
        ) : activeView === "picasso" ? (
          <PicassoLabelerPanel adminSecret={password} />
        ) : activeView === "shutdown" ? (
          <ShutdownPanel socket={socketRef.current} minutes={shutdownMinutes} setMinutes={setShutdownMinutes} message={shutdownMessage} setMessage={setShutdownMessage} />
        ) : selectedLobby ? (
          <LobbyDetail lobby={selectedLobby} socket={socketRef.current!} messageReplies={messageReplies.filter(r => r.lobbyKey === selectedLobby.lobbyKey)} />
        ) : (
          <Box display="flex" alignItems="center" justifyContent="center" height="100%">
            <Typography color="text.secondary">Select a lobby</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Box display="flex" height="100vh" overflow="hidden">
      <RepliesDialog
        open={repliesOpen}
        onClose={closeReplies}
        replies={messageReplies}
        onSelectLobby={handleSelectLobby}
        onClear={clearReplies}
        lastSeenTs={lastSeenReplyTs}
      />
      {isMobile ? (
        <>
          <Drawer
            anchor="left"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            PaperProps={{
              sx: {
                width: SIDEBAR_WIDTH,
                background: "rgba(255,255,255,0.88)",
              },
            }}
          >
            <SidebarContent
              {...sidebarProps}
              onClose={() => setDrawerOpen(false)}
              viewNav={<ViewNav activeView={activeView} onSelect={switchView} stacked />}
            />
          </Drawer>
          {mainContent}
        </>
      ) : (
        <>
          {/* Desktop permanent sidebar */}
          <Box
            width={SIDEBAR_WIDTH}
            flexShrink={0}
            display="flex"
            flexDirection="column"
            overflow="hidden"
            sx={{
              background: "rgba(255,255,255,0.88)",
              borderRight: "1px solid rgba(255,255,255,0.5)",
            }}
          >
            <SidebarContent {...sidebarProps} />
          </Box>
          {mainContent}
        </>
      )}
    </Box>
  );
}
